import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Upload, Play, Pause, User, Loader2, Save, Layers, Settings, Image as ImageIcon,
  Sparkles, Grid3X3, Bone, SlidersHorizontal, Undo2, Redo2, ChevronRight, Trash2, FolderOpen
} from 'lucide-react';
import ThreeCanvas from '../components/ThreeCanvas';
import SpriteRenderer from '../components/SpriteRenderer';
import AssetLibrary from '../components/AssetLibrary';
import ClipTimeline from '../components/ClipTimeline';
import ClipSettingsPanel from '../components/ClipSettingsPanel';
// CharacterSettingsPanel removed - settings now controlled via timeline tracks
import BoneInspector from '../components/BoneInspector';
import { analyzeAndBakeRootMotion } from '../utils/rootMotionAnalyzer';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Camera presets
const CAMERA_PRESETS = {
  front: { theta: 0, phi: 75, distance: 3, fov: 50, name: 'Front' },
  back: { theta: 180, phi: 75, distance: 3, fov: 50, name: 'Back' },
  left: { theta: -90, phi: 75, distance: 3, fov: 50, name: 'Left' },
  right: { theta: 90, phi: 75, distance: 3, fov: 50, name: 'Right' },
  threeQuarter: { theta: 35, phi: 70, distance: 3.5, fov: 45, name: '3/4 View' },
  closeUp: { theta: 10, phi: 80, distance: 1.5, fov: 35, name: 'Close-Up' },
};

// Built-in animation templates
// Default animation templates - fallback if API fails
const DEFAULT_ANIMATIONS = {
  essentials: [
    { id: 'walk', name: 'Walk', total_frames: 48, fps: 24, is_stock: true, is_builtin: true },
    { id: 'run', name: 'Run', total_frames: 24, fps: 24, is_stock: true, is_builtin: true },
    { id: 'punch', name: 'Punch', total_frames: 24, fps: 24, is_stock: true, is_builtin: true },
  ],
  signature: [
    { id: 'signature-template', name: 'Signature', total_frames: 240, fps: 24, is_blank_template: true },
  ],
  community: []
};

// ============== CREATE CHARACTER MODAL ==============
function CreateCharacterModal({ isOpen, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      if (!name) {
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        setName(baseName);
      }
    }
  };

  const handleCreate = async () => {
    if (!imageFile || !name.trim()) return;
    
    setCreating(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('name', name.trim());

      const createResp = await axios.post(`${API}/api/3d-lab/create-from-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const projectId = createResp.data.project_id;
      await axios.post(`${API}/api/3d-lab/project/${projectId}/convert`);

      onCreated(projectId);
      onClose();
      setName('');
      setImageFile(null);
      setImagePreview(null);
    } catch (err) {
      console.error('Failed to create character:', err);
      setError(err.response?.data?.detail || 'Failed to create character');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setName('');
      setImageFile(null);
      setImagePreview(null);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Character</DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload a character image to convert it to an animated 3D model.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${imagePreview ? 'border-purple-500 bg-purple-500/10' : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'}`}
          >
            {imagePreview ? (
              <div className="space-y-2">
                <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                <p className="text-sm text-slate-400">Click to change image</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 mx-auto text-slate-500" />
                <p className="text-sm text-slate-400">Click to upload character image</p>
                <p className="text-xs text-slate-500">PNG, JPG up to 10MB</p>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
          </div>

          <div>
            <label className="text-sm text-slate-400 block mb-1">Character Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter character name"
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400">
            <p className="font-medium text-slate-300 mb-1">What happens next:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Image converts to 3D model (~2 min)</li>
              <li>Skeleton & animations added (~2 min)</li>
              <li>Cartoon style applied (~2 min)</li>
            </ol>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={creating} className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!imageFile || !name.trim() || creating} className="flex-1 bg-purple-600 hover:bg-purple-700">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Sparkles className="w-4 h-4 mr-2" />Create Character</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============== CANVAS TOOLBAR ==============
function CanvasToolbar({ 
  character, 
  isPlaying, 
  setIsPlaying, 
  showGrid, 
  setShowGrid, 
  camera, 
  setCamera,
  selectedBone,
  setSelectedBone,
  bones,
  showInspector,
  setShowInspector,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSave,
  isSaving,
  lastSaved,
  renderMode,
  setRenderMode
}) {
  return (
    <div className="h-12 bg-slate-800 border-b border-slate-700 flex items-center px-3 gap-2">
      <span className="text-sm font-medium text-white mr-2">{character?.name || 'No character'}</span>
      
      <div className="h-6 w-px bg-slate-600 mx-2" />
      
      {/* Undo/Redo */}
      <Button
        size="sm"
        variant="ghost"
        onClick={onUndo}
        disabled={!canUndo}
        className="h-8 w-8 p-0 text-slate-300 hover:text-white disabled:opacity-30"
        title="Undo (Ctrl+Z)"
        data-testid="undo-btn"
      >
        <Undo2 className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onRedo}
        disabled={!canRedo}
        className="h-8 w-8 p-0 text-slate-300 hover:text-white disabled:opacity-30"
        title="Redo (Ctrl+Shift+Z)"
        data-testid="redo-btn"
      >
        <Redo2 className="w-4 h-4" />
      </Button>

      <div className="h-6 w-px bg-slate-600 mx-2" />
      
      {/* Play/Pause */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsPlaying(!isPlaying)}
        className="h-8 w-8 p-0 text-slate-300 hover:text-white"
        data-testid="play-pause-btn"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>

      <div className="h-6 w-px bg-slate-600 mx-2" />

      {/* Bone dropdown */}
      <Select value={selectedBone || ''} onValueChange={setSelectedBone}>
        <SelectTrigger className="w-40 h-8 bg-slate-700 border-slate-600 text-xs">
          <SelectValue placeholder="Select bone..." />
        </SelectTrigger>
        <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
          {bones.map((bone) => (
            <SelectItem key={bone} value={bone} className="text-xs">{bone}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Inspector toggle */}
      <Button
        size="sm"
        variant={showInspector ? "secondary" : "ghost"}
        onClick={() => setShowInspector(!showInspector)}
        className="h-8 px-2 text-slate-300"
        title="Toggle Inspector Panel"
      >
        <SlidersHorizontal className="w-4 h-4" />
      </Button>

      <div className="h-6 w-px bg-slate-600 mx-2" />

      {/* Grid toggle */}
      <Button
        size="sm"
        variant={showGrid ? "secondary" : "ghost"}
        onClick={() => setShowGrid(!showGrid)}
        className="h-8 px-2 text-slate-300"
      >
        <Grid3X3 className="w-4 h-4 mr-1" />
        Grid
      </Button>

      {/* Camera presets */}
      <div className="flex items-center gap-1 ml-2">
        <span className="text-xs text-slate-400">Camera:</span>
        <select
          value={`${camera.theta}-${camera.phi}`}
          onChange={(e) => {
            const preset = Object.values(CAMERA_PRESETS).find(p => `${p.theta}-${p.phi}` === e.target.value);
            if (preset) setCamera(preset);
          }}
          className="bg-slate-700 text-white text-xs rounded px-1 py-0.5 border-none"
        >
          {Object.entries(CAMERA_PRESETS).map(([key, preset]) => (
            <option key={key} value={`${preset.theta}-${preset.phi}`}>{preset.name}</option>
          ))}
        </select>
      </div>

      <div className="flex-1" />

      {/* 2D/3D Render Mode Toggle */}
      <div className="flex items-center bg-slate-800 rounded-md p-0.5 mr-2">
        <Button
          size="sm"
          variant={renderMode === '3d' ? 'secondary' : 'ghost'}
          onClick={() => setRenderMode('3d')}
          className="h-7 px-2 text-xs"
          title="3D View"
        >
          <Bone className="w-3.5 h-3.5 mr-1" />
          3D
        </Button>
        <Button
          size="sm"
          variant={renderMode === '2d-sprite' ? 'secondary' : 'ghost'}
          onClick={() => setRenderMode('2d-sprite')}
          className="h-7 px-2 text-xs"
          title="2D Sprite View"
        >
          <Layers className="w-3.5 h-3.5 mr-1" />
          2D
        </Button>
      </div>

      {/* Save Button */}
      <Button
        size="sm"
        onClick={onSave}
        disabled={isSaving}
        className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white gap-2"
        data-testid="save-shot-btn"
      >
        {isSaving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Save className="w-4 h-4" />
        )}
        Save
      </Button>
      
      {lastSaved && (
        <span className="text-xs text-slate-400 ml-2">
          Saved {lastSaved.toLocaleTimeString()}
        </span>
      )}

      {character && (
        <Badge variant="outline" className="text-green-400 border-green-400/50">
          {character.current_style || 'styled'}
        </Badge>
      )}
    </div>
  );
}

// ============== MAIN COMPONENT ==============
export default function AnimationStudio() {
  // Auth hook for user info
  const { user } = useAuth();
  
  // Characters state
  const [characters, setCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Canvas state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [camera, setCamera] = useState(CAMERA_PRESETS.threeQuarter);
  const [renderMode, setRenderMode] = useState('3d'); // '3d' | '2d-sprite'
  
  // 2D Sprite Animation state - keyed by character ID for multi-character support
  const [spriteConfigs, setSpriteConfigs] = useState({}); // { characterId: config }
  const spriteConfigsRef = useRef({}); // Ref for stable access in callbacks
  const [showSpriteSetup, setShowSpriteSetup] = useState(false);
  const [selectedSpritePart, setSelectedSpritePart] = useState(null);
  const [spriteSetupLoading, setSpriteSetupLoading] = useState(false);
  const [spriteSetupCharacterId, setSpriteSetupCharacterId] = useState(null); // Which character's sprites we're editing
  const [spriteEditMode, setSpriteEditMode] = useState(false); // Toggle for sprite edit mode (disables animation)
  
  // Bone/posing state - keyed by character ID for multi-character support
  const [selectedBone, setSelectedBone] = useState(null);
  const [bones, setBones] = useState([]);
  const [currentBoneTransforms, setCurrentBoneTransforms] = useState({}); // { characterId: { boneName: transform } }
  const canvasRef = useRef(null);
  
  // Timeline/clip state
  const [clips, setClips] = useState([]);
  const [selectedClip, setSelectedClip] = useState(null);
  const [expandedClipId, setExpandedClipId] = useState(null);
  
  // Animation library refresh trigger
  const [animationLibraryRefresh, setAnimationLibraryRefresh] = useState(0);
  const [currentFrame, setCurrentFrame] = useState(0);
  const totalFrames = 240; // 10 seconds at 24fps (max animation duration)
  
  // Track selection state (for track settings panel)
  const [selectedTrack, setSelectedTrack] = useState(null);
  
  // Live edit values for real-time preview (before keyframe is created)
  const [liveEditValues, setLiveEditValues] = useState({
    position: null,
    rotation: null,
    scale: null,
    visibility: null,
    yOffset: null
  });
  
  // Live shot values for real-time camera/light preview
  const [liveShotValues, setLiveShotValues] = useState({
    camera: null,
    light: null
  });
  
  // Memoized handler for camera mouse controls (prevents unnecessary re-renders)
  const handleCameraMouseChange = useCallback((newCameraSettings) => {
    setLiveShotValues(prev => ({
      ...prev,
      camera: newCameraSettings
    }));
  }, []);
  
  // Recording mode state ('off' | 'all' | 'changes')
  const [recordMode, setRecordMode] = useState('off');
  
  // Panel state
  const [assetLibraryCollapsed, setAssetLibraryCollapsed] = useState(false);
  const [showInspector, setShowInspector] = useState(false);
  const [savedAnimations, setSavedAnimations] = useState([]);
  
  // Animation library - loaded from backend API
  const [animationLibrary, setAnimationLibrary] = useState(DEFAULT_ANIMATIONS);
  const [animationSearchQuery, setAnimationSearchQuery] = useState('');
  
  // Timeline characters (for multi-character support)
  const [timelineCharacters, setTimelineCharacters] = useState([]);
  
  // Character selection (separate from clip selection)
  const [selectedTimelineCharacter, setSelectedTimelineCharacter] = useState(null);
  
  // Character transforms (position, rotation, scale, visibility)
  // Stored per character: { characterId: { position, rotation, scale, visible, rootMotionEnabled } }
  const [characterTransforms, setCharacterTransforms] = useState({});
  
  // Character transform keyframes (for animating character position/rotation)
  // Stored per character: { characterId: [{ frame, position, rotation, scale }] }
  const [characterTransformKeyframes, setCharacterTransformKeyframes] = useState({});
  
  // Shot settings (camera and light) - independent of characters/clips
  const [shotSettings, setShotSettings] = useState({
    camera: { 
      position: { x: 0, y: 2, z: 5 }, 
      rotation: { x: -15, y: 0, z: 0 } // Default slight downward angle
    },
    light: { 
      position: { x: 5, y: 10, z: 5 },
      intensity: 1
    }
  });
  
  // Shot keyframes (for animating camera and light)
  const [shotKeyframes, setShotKeyframes] = useState({
    camera: [],
    light: []
  });
  
  // Playback state
  const [useBuiltInAnimation, setUseBuiltInAnimation] = useState(false);
  const playbackRef = useRef(null);
  
  // Polling interval
  const pollIntervalRef = useRef(null);
  
  // Root Motion Analysis state (inline in Clip Settings)
  const [rootMotionAnalysis, setRootMotionAnalysis] = useState(null);
  const [isAnalyzingRootMotion, setIsAnalyzingRootMotion] = useState(false);
  const [currentAnalysisStride, setCurrentAnalysisStride] = useState(1.3);
  
  // Persisted baked root motion data (loaded from backend)
  // Structure: { animationKey: { positions: [], stride_length, total_frames, fps, analyzed_at } }
  const [bakedRootMotionData, setBakedRootMotionData] = useState({});

  // Studio project persistence
  const [studioProjectId, setStudioProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Undo/Redo History (max 20 states)
  const MAX_HISTORY = 20;
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Get current state snapshot for undo/redo
  const getStateSnapshot = useCallback(() => ({
    clips: JSON.parse(JSON.stringify(clips)),
    timelineCharacters: JSON.parse(JSON.stringify(timelineCharacters)),
    characterTransforms: JSON.parse(JSON.stringify(characterTransforms)),
    characterTransformKeyframes: JSON.parse(JSON.stringify(characterTransformKeyframes)),
  }), [clips, timelineCharacters, characterTransforms, characterTransformKeyframes]);

  // Ref to track historyIndex for stable callback
  const historyIndexRef = useRef(historyIndex);
  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  // Push state to history (called after state changes)
  const pushToHistory = useCallback((snapshot) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    setHistory(prev => {
      // Remove any states after current index (when undoing then making new changes)
      const newHistory = prev.slice(0, historyIndexRef.current + 1);
      // Add new state
      newHistory.push(snapshot);
      // Keep only last MAX_HISTORY states
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, []); // No dependencies - use ref instead

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    
    isUndoRedoAction.current = true;
    const prevState = history[historyIndex - 1];
    if (prevState) {
      setClips(prevState.clips);
      setTimelineCharacters(prevState.timelineCharacters);
      setCharacterTransforms(prevState.characterTransforms);
      setCharacterTransformKeyframes(prevState.characterTransformKeyframes);
      setHistoryIndex(historyIndex - 1);
      toast.info('Undo');
    }
  }, [history, historyIndex]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    
    isUndoRedoAction.current = true;
    const nextState = history[historyIndex + 1];
    if (nextState) {
      setClips(nextState.clips);
      setTimelineCharacters(nextState.timelineCharacters);
      setCharacterTransforms(nextState.characterTransforms);
      setCharacterTransformKeyframes(nextState.characterTransformKeyframes);
      setHistoryIndex(historyIndex + 1);
      toast.info('Redo');
    }
  }, [history, historyIndex]);

  // Keyboard shortcuts for Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Undo: Ctrl+Z (Windows) or Cmd+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Shift+Z (Windows) or Cmd+Shift+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      // Redo alternative: Ctrl+Y (Windows)
      if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Track state changes and push to history (debounced)
  const historyDebounceRef = useRef(null);
  useEffect(() => {
    // Skip if this is an undo/redo action
    if (isUndoRedoAction.current) return;
    
    // Clear existing timeout
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
    }
    
    // Debounce history push (500ms after last change)
    historyDebounceRef.current = setTimeout(() => {
      const snapshot = getStateSnapshot();
      // Only push if there's actual content
      if (snapshot.clips.length > 0 || snapshot.timelineCharacters.length > 0) {
        pushToHistory(snapshot);
      }
    }, 500);
    
    return () => {
      if (historyDebounceRef.current) {
        clearTimeout(historyDebounceRef.current);
      }
    };
  }, [clips, timelineCharacters, characterTransforms, characterTransformKeyframes, getStateSnapshot, pushToHistory]);

  // Model URL
  const modelUrl = useMemo(() => {
    if (!selectedCharacter?.id) return null;
    return `${API}/api/3d-lab/proxy-model/${selectedCharacter.id}?type=armature`;
  }, [selectedCharacter?.id]);

  const texturedModelUrl = useMemo(() => {
    if (!selectedCharacter?.id) return null;
    return `${API}/api/3d-lab/proxy-model/${selectedCharacter.id}`;
  }, [selectedCharacter?.id]);

  const isCharacterReady = selectedCharacter && ['styled', 'rigged', 'model_ready'].includes(selectedCharacter.status);

  // Load characters
  const loadCharacters = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/3d-lab/projects`);
      const projects = resp.data.projects || [];
      projects.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setCharacters(projects);
      
      setSelectedCharacter(prev => {
        if (!prev) return null;
        const updated = projects.find(p => p.id === prev.id);
        if (!updated) return prev;
        if (updated.status !== prev.status || updated.progress !== prev.progress) {
          return updated;
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to load characters:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load animation library from backend
  const loadAnimationLibrary = useCallback(async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      // Add user_id to get user's signature animations
      if (user?.id) params.append('user_id', user.id);
      
      const resp = await axios.get(`${API}/api/animations?${params}`);
      setAnimationLibrary(resp.data.animations || DEFAULT_ANIMATIONS);
    } catch (err) {
      console.error('Failed to load animation library:', err);
      setAnimationLibrary(DEFAULT_ANIMATIONS);
    }
  }, [user?.id]);

  // Load saved animations (legacy - keeping for compatibility)
  const loadAnimations = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/pose-animation/animations`);
      setSavedAnimations(resp.data.animations || []);
    } catch (err) {
      console.error('Failed to load animations:', err);
    }
  }, []);
  
  // Load baked root motion data for a character/project
  const loadBakedRootMotion = useCallback(async (characterId) => {
    if (!characterId) return;
    try {
      // Use 3D Lab endpoint (character/project level) instead of animation-studio
      const resp = await axios.get(`${API}/api/3d-lab/project/${characterId}/baked-root-motion`);
      const bakedData = resp.data.baked_root_motion || {};
      setBakedRootMotionData(bakedData);
      console.log(`Loaded baked root motion for ${Object.keys(bakedData).length} animations`);
    } catch (err) {
      // Project might not have any baked data yet, that's OK
      console.log('No baked root motion data found for this character');
      setBakedRootMotionData({});
    }
  }, []);
  
  // Save baked root motion data to backend
  const saveBakedRootMotion = useCallback(async (animationKey, bakedData) => {
    if (!selectedCharacter?.id || !animationKey || !bakedData) return;
    try {
      // Use 3D Lab endpoint (character/project level) instead of animation-studio
      await axios.put(`${API}/api/3d-lab/project/${selectedCharacter.id}/baked-root-motion`, {
        animation_key: animationKey,
        baked_data: {
          positions: bakedData.positions || bakedData.bakedRootMotionPositions || [],
          stride_length: bakedData.strideLength || bakedData.stride_length || 1.3,
          total_frames: bakedData.totalFrames || bakedData.total_frames || 48,
          fps: bakedData.fps || 24,
          analyzed_at: new Date().toISOString()
        }
      });
      
      // Update local state
      setBakedRootMotionData(prev => ({
        ...prev,
        [animationKey]: bakedData
      }));
      
      console.log(`Saved baked root motion for animation: ${animationKey}`);
    } catch (err) {
      console.error('Failed to save baked root motion:', err);
      toast.error('Failed to save root motion data');
    }
  }, [selectedCharacter?.id]);
  
  // Save the entire studio session to backend
  const saveStudioSession = useCallback(async () => {
    if (!user?.id) {
      toast.error('Please login to save');
      return;
    }
    
    setIsSaving(true);
    try {
      const sessionData = {
        user_id: user.id,
        clips: clips,
        timeline_characters: timelineCharacters,
        character_transforms: characterTransforms,
        character_transform_keyframes: characterTransformKeyframes,
        shot_settings: shotSettings,
        shot_keyframes: shotKeyframes,
        selected_character_id: selectedCharacter?.id || null,
        current_frame: currentFrame,
        total_frames: totalFrames,
        updated_at: new Date().toISOString()
      };
      
      if (studioProjectId) {
        // Update existing session
        await axios.put(`${API}/api/animation-studio/session/${studioProjectId}`, sessionData);
      } else {
        // Create new session
        const resp = await axios.post(`${API}/api/animation-studio/session`, sessionData);
        setStudioProjectId(resp.data.id);
      }
      
      setLastSaved(new Date());
      toast.success('Shot saved successfully!');
    } catch (err) {
      console.error('Failed to save studio session:', err);
      toast.error('Failed to save shot');
    } finally {
      setIsSaving(false);
    }
  }, [user?.id, clips, timelineCharacters, characterTransforms, characterTransformKeyframes, 
      shotSettings, shotKeyframes, selectedCharacter?.id, currentFrame, totalFrames, studioProjectId]);
  
  // Keep spriteConfigsRef in sync with state
  useEffect(() => {
    spriteConfigsRef.current = spriteConfigs;
  }, [spriteConfigs]);
  
  // Load or create sprite config for a specific character
  const loadOrCreateSpriteConfig = useCallback(async (character) => {
    if (!user?.id || !character) return null;
    
    // Check if we already have config for this character (use ref to avoid stale closure)
    if (spriteConfigsRef.current[character.id]) {
      return spriteConfigsRef.current[character.id];
    }
    
    setSpriteSetupLoading(true);
    try {
      // First, try to find existing config by character_profile_id (finds sprites from any user)
      const charResp = await axios.get(`${API}/api/sprite-animation/configs/character/${character.id}`);
      if (charResp.data.found && charResp.data.config) {
        const existingConfig = charResp.data.config;
        const totalSprites = Object.values(existingConfig.parts || {}).reduce(
          (sum, p) => sum + (p.angle_buckets?.length || 0), 0
        );
        if (totalSprites > 0) {
          console.log(`Found existing sprite config with ${totalSprites} sprites for ${character.name}`);
          setSpriteConfigs(prev => ({ ...prev, [character.id]: existingConfig }));
          return existingConfig;
        }
      }
      
      // Fallback: Try to find config for current user
      const resp = await axios.get(`${API}/api/sprite-animation/configs/user/${user.id}`);
      const configs = resp.data.configs || [];
      
      // Find config matching this character
      const existingConfig = configs.find(c => 
        c.character_name === character.name || 
        c.character_profile_id === character.id
      );
      
      if (existingConfig) {
        setSpriteConfigs(prev => ({ ...prev, [character.id]: existingConfig }));
        return existingConfig;
      }
      
      // Create new config from humanoid template
      const createResp = await axios.post(
        `${API}/api/sprite-animation/config/from-template?user_id=${user.id}&character_name=${encodeURIComponent(character.name)}&character_profile_id=${character.id}`
      );
      
      if (createResp.data.success) {
        setSpriteConfigs(prev => ({ ...prev, [character.id]: createResp.data.config }));
        toast.success(`Created sprite configuration for ${character.name}`);
        return createResp.data.config;
      }
    } catch (err) {
      console.error('Failed to load/create sprite config:', err);
      toast.error('Failed to load sprite configuration');
    } finally {
      setSpriteSetupLoading(false);
    }
    return null;
  }, [user?.id]);
  
  // Upload sprite for a body part angle (for current setup character)
  const uploadSpriteForPart = useCallback(async (partName, angleName, rangeMin, rangeMax, file) => {
    const configId = spriteSetupCharacterId && spriteConfigs[spriteSetupCharacterId]?.id;
    if (!configId) return;
    
    const formData = new FormData();
    formData.append('part_name', partName);
    formData.append('angle_name', angleName);
    formData.append('range_min', rangeMin);
    formData.append('range_max', rangeMax);
    formData.append('z_order_modifier', 0);
    formData.append('file', file);
    
    try {
      const resp = await axios.post(
        `${API}/api/sprite-animation/config/${configId}/sprite`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (resp.data.success) {
        // Reload config to get updated sprite URLs
        const configResp = await axios.get(`${API}/api/sprite-animation/config/${configId}`);
        setSpriteConfigs(prev => ({ ...prev, [spriteSetupCharacterId]: configResp.data }));
        toast.success(`Sprite uploaded for ${partName} - ${angleName}`);
      }
    } catch (err) {
      console.error('Failed to upload sprite:', err);
      toast.error('Failed to upload sprite');
    }
  }, [spriteSetupCharacterId, spriteConfigs]);
  
  // Track which characters we've attempted to load sprite configs for (to prevent infinite loops)
  const spriteConfigLoadAttemptedRef = useRef(new Set());
  
  // Load sprite configs for all timeline characters when switching to 2D mode
  useEffect(() => {
    if (renderMode === '2d-sprite') {
      // Clear cache to force reload of configs (in case sprites were uploaded elsewhere)
      spriteConfigLoadAttemptedRef.current.clear();
      
      // Load config for selected character first
      if (selectedCharacter && !spriteConfigLoadAttemptedRef.current.has(selectedCharacter.id)) {
        spriteConfigLoadAttemptedRef.current.add(selectedCharacter.id);
        loadOrCreateSpriteConfig(selectedCharacter);
      }
      // Also load for all timeline characters
      timelineCharacters.forEach(tc => {
        if (!spriteConfigLoadAttemptedRef.current.has(tc.characterId)) {
          spriteConfigLoadAttemptedRef.current.add(tc.characterId);
          const char = characters.find(c => c.id === tc.characterId);
          if (char) {
            loadOrCreateSpriteConfig(char);
          }
        }
      });
    }
  }, [renderMode, selectedCharacter, timelineCharacters, characters, loadOrCreateSpriteConfig]);

  // Initial load
  useEffect(() => {
    loadCharacters();
    loadAnimations();
    loadAnimationLibrary();
  }, [loadAnimationLibrary]);

  // Load baked root motion when character is selected
  useEffect(() => {
    if (selectedCharacter?.id) {
      loadBakedRootMotion(selectedCharacter.id);
    }
  }, [selectedCharacter?.id, loadBakedRootMotion]);

  // Track previous frame to detect when user manually moves playhead
  const prevFrameRef = useRef(currentFrame);
  
  // Clear live shot values when playhead is moved significantly (not during live editing)
  // This ensures interpolated values show at new positions
  useEffect(() => {
    const frameDiff = Math.abs(currentFrame - prevFrameRef.current);
    // Only clear if frame jumped by more than 1 (user clicked timeline, not live editing)
    if (frameDiff > 1) {
      setLiveShotValues({ camera: null, light: null });
    }
    prevFrameRef.current = currentFrame;
  }, [currentFrame]);

  // Poll for processing characters
  useEffect(() => {
    const hasProcessing = characters.some(c => 
      ['converting', 'rigging', 'retexturing', 'created'].includes(c.status)
    );

    if (hasProcessing) {
      pollIntervalRef.current = setInterval(loadCharacters, 3000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [characters, loadCharacters]);

  // Playback loop
  useEffect(() => {
    if (!isPlaying) {
      if (playbackRef.current) {
        cancelAnimationFrame(playbackRef.current);
        playbackRef.current = null;
      }
      return;
    }

    let lastTime = performance.now();
    const fps = 24;
    const frameDuration = 1000 / fps;

    const loop = () => {
      const now = performance.now();
      const elapsed = now - lastTime;
      
      if (elapsed >= frameDuration) {
        lastTime = now - (elapsed % frameDuration);
        setCurrentFrame(prev => {
          const next = prev + 1;
          return next >= totalFrames ? 0 : next;
        });
      }
      
      playbackRef.current = requestAnimationFrame(loop);
    };

    playbackRef.current = requestAnimationFrame(loop);

    return () => {
      if (playbackRef.current) {
        cancelAnimationFrame(playbackRef.current);
      }
    };
  }, [isPlaying, totalFrames]);

  // Get active clip at current frame
  const activeClip = useMemo(() => {
    if (!selectedCharacter) {
      return null;
    }
    
    const clip = clips.find(c => 
      c.characterId === selectedCharacter.id &&
      currentFrame >= c.startFrame &&
      currentFrame < c.startFrame + c.duration
    );
    
    return clip;
  }, [clips, currentFrame, selectedCharacter]);

  // Calculate local frame within clip and get keyframes
  const { clipLocalFrame, clipKeyframes, useClipKeyframes } = useMemo(() => {
    if (!activeClip) {
      return { clipLocalFrame: 0, clipKeyframes: [], useClipKeyframes: false };
    }
    
    const localFrame = currentFrame - activeClip.startFrame;
    const animation = activeClip.animation;
    const speed = activeClip.speed || 1;
    
    // Handle looping - wrap the local frame within the base duration
    const baseDuration = animation?.total_frames || 48;
    
    // Apply speed: multiply localFrame by speed to get effective frame in keyframe space
    // At 2x speed: localFrame 12 maps to keyframe frame 24
    // At 0.5x speed: localFrame 48 maps to keyframe frame 24
    const effectiveFrame = localFrame * speed;
    const loopedFrame = effectiveFrame % baseDuration;
    
    // Get keyframes from the animation
    const keyframes = animation?.keyframes || [];
    
    // If animation has keyframes, use them
    if (keyframes.length > 0) {
      return {
        clipLocalFrame: loopedFrame,
        clipKeyframes: keyframes,
        useClipKeyframes: true
      };
    }
    
    return { clipLocalFrame: loopedFrame, clipKeyframes: [], useClipKeyframes: false };
  }, [activeClip, currentFrame]);

  // ============== NEW TRUE ROOT MOTION SYSTEM ==============
  // Uses delta-based extraction from baked root motion data
  // Tracks accumulated position across clips for seamless transitions
  
  // Helper to get baked positions for an animation (from clip or persisted data)
  const getBakedPositionsForAnimation = useCallback((animation) => {
    if (!animation) return null;
    
    // First check if clip's animation already has baked data
    if (animation.rootMotionBaked && animation.bakedRootMotionPositions?.length > 0) {
      return {
        positions: animation.bakedRootMotionPositions,
        strideLength: animation.strideLength || 1.3
      };
    }
    
    // Then check persisted baked data by animation ID or name
    const animKey = animation.id || animation.name?.toLowerCase().replace(/\s+/g, '_');
    if (animKey && bakedRootMotionData[animKey]) {
      const persisted = bakedRootMotionData[animKey];
      return {
        positions: persisted.positions || [],
        strideLength: persisted.stride_length || persisted.strideLength || 1.3
      };
    }
    
    // Also check by common animation names (walk, run, etc.)
    const commonNames = ['walk', 'run', 'idle', 'jump'];
    for (const name of commonNames) {
      if (animation.name?.toLowerCase().includes(name) && bakedRootMotionData[name]) {
        const persisted = bakedRootMotionData[name];
        return {
          positions: persisted.positions || [],
          strideLength: persisted.stride_length || persisted.strideLength || 1.3
        };
      }
    }
    
    return null;
  }, [bakedRootMotionData]);
  
  // Helper function to calculate end position of a clip
  const calculateClipEndPosition = useCallback((clip, charTransforms) => {
    if (!clip) return { x: 0, y: 0, z: 0 };
    
    const clipCharacterId = clip.characterId;
    const transform = charTransforms[clipCharacterId];
    const rootMotionEnabled = transform?.rootMotionEnabled ?? true;
    
    const animation = clip.animation;
    const isBlankTemplate = animation?.is_blank_template && (!animation?.keyframes || animation.keyframes.length === 0);
    
    // No movement for blank templates or disabled root motion
    if (!rootMotionEnabled || isBlankTemplate) {
      return { x: 0, y: 0, z: 0 };
    }
    
    // Get baked root motion data (from clip or persisted)
    const bakedData = getBakedPositionsForAnimation(animation);
    const bakedPositions = bakedData?.positions;
    const strideLength = bakedData?.strideLength || animation?.strideLength || 1.3;
    const speed = clip.speed || 1;
    const baseDuration = animation?.total_frames || 48;
    
    // Calculate total effective frames in this clip
    const clipDuration = clip.duration || baseDuration;
    const effectiveFrames = clipDuration * speed;
    
    // Apply in the direction of character rotation
    const rotationRad = THREE.MathUtils.degToRad(transform?.rotation?.y || 0);
    
    if (bakedPositions && bakedPositions.length > 0) {
      // Calculate total cycles completed in this clip
      const totalCycles = effectiveFrames / baseDuration;
      const completedCycles = Math.floor(totalCycles);
      const remainingFrame = (effectiveFrames % baseDuration);
      
      // Get position at the end of the final partial cycle
      const floorFrame = Math.floor(remainingFrame);
      const ceilFrame = Math.ceil(remainingFrame) % baseDuration;
      const t = remainingFrame - floorFrame;
      
      const floorPos = bakedPositions[floorFrame] || bakedPositions[0];
      const ceilPos = bakedPositions[ceilFrame] || bakedPositions[0];
      
      const posInCycleZ = floorPos.z + (ceilPos.z - floorPos.z) * t;
      
      // Total distance = cycles completed * stride + current position in cycle
      const totalDistance = completedCycles * strideLength + posInCycleZ;
      
      const worldX = Math.sin(rotationRad) * totalDistance;
      const worldZ = Math.cos(rotationRad) * totalDistance;
      
      // Root motion only affects XZ - character stays grounded
      return { x: worldX, y: 0, z: worldZ };
    }
    
    // Fallback: Simple constant velocity calculation
    const totalCycles = effectiveFrames / baseDuration;
    const distance = totalCycles * strideLength;
    
    return { 
      x: Math.sin(rotationRad) * distance, 
      y: 0, 
      z: Math.cos(rotationRad) * distance 
    };
  }, [getBakedPositionsForAnimation]);

  // Calculate accumulated position from all PREVIOUS clips for this character
  const accumulatedPreviousClipOffset = useMemo(() => {
    if (!activeClip || !selectedCharacter) {
      return { x: 0, y: 0, z: 0 };
    }
    
    // Find all clips for this character that END before the current clip starts
    const characterClips = clips
      .filter(c => c.characterId === selectedCharacter.id && c.id !== activeClip.id)
      .sort((a, b) => a.startFrame - b.startFrame);
    
    // Sum up the end positions of all clips that come BEFORE the active clip
    let accumulated = { x: 0, y: 0, z: 0 };
    
    for (const clip of characterClips) {
      // Only include clips that end before or at the start of active clip
      if (clip.startFrame + (clip.duration || clip.animation?.total_frames || 48) <= activeClip.startFrame) {
        const clipEndPos = calculateClipEndPosition(clip, characterTransforms);
        accumulated.x += clipEndPos.x;
        accumulated.y += clipEndPos.y;
        accumulated.z += clipEndPos.z;
      }
    }
    
    return accumulated;
  }, [activeClip, selectedCharacter, clips, characterTransforms, calculateClipEndPosition]);

  // Calculate root motion position offset using TRUE DELTA EXTRACTION
  // ========== TRUE ROOT MOTION OFFSET CALCULATION ==========
  // Uses the actual hip bone movement extracted from the animation
  const rootMotionOffset = useMemo(() => {
    if (!activeClip || !selectedCharacter) {
      return { x: 0, y: 0, z: 0 };
    }
    
    const clipCharacterId = activeClip.characterId || selectedCharacter.id;
    const transform = characterTransforms[clipCharacterId];
    const rootMotionEnabled = transform?.rootMotionEnabled ?? true;
    const rootMotionMode = transform?.rootMotionMode || 'normal';
    
    const animation = activeClip.animation;
    const isBlankTemplate = animation?.is_blank_template && (!animation?.keyframes || animation.keyframes.length === 0);
    
    // For blank templates, disabled root motion, or TREADMILL mode, return accumulated position only
    if (!rootMotionEnabled || isBlankTemplate || rootMotionMode === 'treadmill') {
      return accumulatedPreviousClipOffset;
    }
    
    // Calculate frame info
    const localFrame = currentFrame - activeClip.startFrame;
    const speed = activeClip.speed || 1;
    const effectiveFrame = localFrame * speed;
    const baseDuration = animation?.total_frames || 48;
    
    // Character's world rotation (Y axis)
    const rotationRad = THREE.MathUtils.degToRad(transform?.rotation?.y || 0);
    
    let currentClipOffset = { x: 0, y: 0, z: 0 };
    
    // === TRUE ROOT MOTION from extracted animation ===
    const rootMotionTrack = animation?.rootMotionTrack;
    const rootMotion = animation?.rootMotion;
    
    // Get positions array from either format
    const positions = rootMotionTrack?.positions || rootMotion?.framePositions;
    // Get Y offset keyframes (manually defined per animation)
    const yOffsetKeyframes = rootMotionTrack?.yOffsetKeyframes || rootMotion?.yOffsetKeyframes || [];
    const isEnabled = rootMotionTrack?.enabled ?? rootMotion?.enabled ?? false;
    
    if (isEnabled && positions && positions.length > 0) {
      // Get cycle distance from the LAST position in the animation
      // This ensures smooth looping - frame 0 of next cycle starts exactly where last frame ended
      const lastPos = positions[positions.length - 1] || { x: 0, y: 0, z: 0 };
      const cycleDistanceX = lastPos.x;
      const cycleDistanceZ = lastPos.z;
      const actualCycleDistance = Math.sqrt(cycleDistanceX * cycleDistanceX + cycleDistanceZ * cycleDistanceZ);
      
      // Check if the actual hip movement is too small (in-place animation)
      const configuredDistance = rootMotionTrack?.totalDistance || rootMotion?.distancePerCycle || 1.3;
      const isInPlaceAnimation = actualCycleDistance < 0.1;
      
      // Calculate which cycle we're in
      const cycleNumber = Math.floor(effectiveFrame / baseDuration);
      // Progress within current cycle (0 to 1)
      const progressInCycle = (effectiveFrame % baseDuration) / baseDuration;
      
      // Calculate frame position for interpolation
      const frameInCycle = effectiveFrame % baseDuration;
      const frameFloat = frameInCycle * positions.length / baseDuration;
      const floorFrame = Math.floor(frameFloat);
      const ceilFrame = Math.min(floorFrame + 1, positions.length - 1);
      const t = frameFloat - floorFrame;
      
      // Y OFFSET: Use liveEditValues if editing, otherwise interpolate from keyframes
      let groundY = 0;
      if (liveEditValues.yOffset !== null && liveEditValues.yOffset !== undefined) {
        // Use live edit value for real-time preview
        groundY = liveEditValues.yOffset;
      } else if (yOffsetKeyframes && yOffsetKeyframes.length > 0) {
        // Find surrounding keyframes for current frame in cycle
        let prevKf = yOffsetKeyframes[0];
        let nextKf = yOffsetKeyframes[yOffsetKeyframes.length - 1];
        
        for (let i = 0; i < yOffsetKeyframes.length - 1; i++) {
          if (yOffsetKeyframes[i].frame <= frameInCycle && yOffsetKeyframes[i + 1].frame >= frameInCycle) {
            prevKf = yOffsetKeyframes[i];
            nextKf = yOffsetKeyframes[i + 1];
            break;
          }
        }
        
        // Interpolate between keyframes
        const kfRange = nextKf.frame - prevKf.frame;
        if (kfRange > 0) {
          const kfT = (frameInCycle - prevKf.frame) / kfRange;
          groundY = prevKf.offset + (nextKf.offset - prevKf.offset) * kfT;
        } else {
          groundY = prevKf.offset;
        }
      }
      
      if (isInPlaceAnimation && configuredDistance > 0.1) {
        // === IN-PLACE ANIMATION: Use linear interpolation with configured distance ===
        const totalProgress = cycleNumber + progressInCycle;
        const totalDistance = totalProgress * configuredDistance;
        
        // Apply character's world rotation
        const cosR = Math.cos(rotationRad);
        const sinR = Math.sin(rotationRad);
        
        currentClipOffset = {
          x: sinR * totalDistance,
          y: groundY,  // Y offset from keyframes
          z: cosR * totalDistance
        };
      } else {
        // === ACTUAL HIP MOVEMENT: Use extracted XZ positions with smooth looping ===
        const p1 = positions[floorFrame] || { x: 0, y: 0, z: 0 };
        const p2 = positions[ceilFrame] || p1;
        
        // Interpolated XZ position within current cycle
        const posInCycleX = p1.x + (p2.x - p1.x) * t;
        const posInCycleZ = p1.z + (p2.z - p1.z) * t;
        
        // Total position = completed cycles * exact cycle distance + current position
        // Using lastPos as cycle distance ensures no discontinuity at loop boundary
        const totalX = cycleNumber * cycleDistanceX + posInCycleX;
        const totalZ = cycleNumber * cycleDistanceZ + posInCycleZ;
        
        // Apply character's world rotation
        const cosR = Math.cos(rotationRad);
        const sinR = Math.sin(rotationRad);
        
        currentClipOffset = {
          x: totalX * cosR + totalZ * sinR,
          y: groundY,  // Foot grounding offset
          z: -totalX * sinR + totalZ * cosR
        };
      }
      
    } else if (animation?.rootMotionBaked && animation?.bakedRootMotionPositions?.length > 0) {
      // === LEGACY: Use old baked root motion format ===
      const bakedPositions = animation.bakedRootMotionPositions;
      const strideLength = animation.strideLength || 1.3;
      
      const normalizedFrame = ((effectiveFrame % baseDuration) + baseDuration) % baseDuration;
      const floorFrame = Math.floor(normalizedFrame);
      const ceilFrame = Math.ceil(normalizedFrame) % baseDuration;
      const t = normalizedFrame - floorFrame;
      
      const floorPos = bakedPositions[floorFrame] || bakedPositions[0];
      const ceilPos = bakedPositions[ceilFrame] || bakedPositions[0];
      
      const posInCycleZ = floorPos.z + (ceilPos.z - floorPos.z) * t;
      
      const cycleNumber = Math.floor(effectiveFrame / baseDuration);
      const totalDistance = cycleNumber * strideLength + posInCycleZ;
      
      currentClipOffset = { 
        x: Math.sin(rotationRad) * totalDistance, 
        y: 0,  // Character stays grounded - hip bobbing is in bone animation
        z: Math.cos(rotationRad) * totalDistance 
      };
    }
    // If no root motion data, character stays in place (currentClipOffset remains 0,0,0)
    
    // Return accumulated position from previous clips PLUS current clip offset
    return {
      x: accumulatedPreviousClipOffset.x + currentClipOffset.x,
      y: currentClipOffset.y,  // Use foot grounding offset
      z: accumulatedPreviousClipOffset.z + currentClipOffset.z
    };
  }, [activeClip, currentFrame, selectedCharacter, characterTransforms, accumulatedPreviousClipOffset, liveEditValues.yOffset]);
  
  // ============== TRANSFORM KEYFRAME INTERPOLATION ==============
  // Interpolate position, rotation, scale, visibility from transform keyframes
  const interpolatedTransform = useMemo(() => {
    const defaultTransform = {
      position: null,   // null means use root motion or base transform
      rotation: null,   // null means use base character rotation
      scale: null,      // null means use base character scale
      visibility: null  // null means use base character visibility
    };
    
    if (!activeClip?.animation?.transformKeyframes?.length) {
      return defaultTransform;
    }
    
    const transformKfs = activeClip.animation.transformKeyframes;
    const localFrame = currentFrame - activeClip.startFrame;
    const speed = activeClip.speed || 1;
    const effectiveFrame = localFrame * speed;
    
    // Pre-group and sort keyframes by track type (optimization)
    const keyframesByTrack = {};
    for (const kf of transformKfs) {
      const track = kf.trackType;
      if (!keyframesByTrack[track]) {
        keyframesByTrack[track] = [];
      }
      keyframesByTrack[track].push(kf);
    }
    // Sort each track's keyframes by frame
    for (const track in keyframesByTrack) {
      keyframesByTrack[track].sort((a, b) => a.frame - b.frame);
    }
    
    // Helper to interpolate between keyframes for a specific track type
    const interpolateTrack = (trackType, defaultValue) => {
      const trackKfs = keyframesByTrack[trackType];
      if (!trackKfs || trackKfs.length === 0) return defaultValue;
      
      // Find surrounding keyframes using binary search style
      let prevKf = trackKfs[0];
      let nextKf = trackKfs[trackKfs.length - 1];
      
      // Before first keyframe
      if (effectiveFrame < trackKfs[0].frame) {
        return trackKfs[0].value || defaultValue;
      }
      
      // After last keyframe
      if (effectiveFrame >= trackKfs[trackKfs.length - 1].frame) {
        return trackKfs[trackKfs.length - 1].value || defaultValue;
      }
      
      // Find surrounding keyframes
      for (let i = 0; i < trackKfs.length - 1; i++) {
        if (trackKfs[i].frame <= effectiveFrame && trackKfs[i + 1].frame > effectiveFrame) {
          prevKf = trackKfs[i];
          nextKf = trackKfs[i + 1];
          break;
        }
      }
      
      // Interpolate
      const range = nextKf.frame - prevKf.frame;
      if (range === 0) return prevKf.value || defaultValue;
      
      const t = (effectiveFrame - prevKf.frame) / range;
      const prevVal = prevKf.value || defaultValue;
      const nextVal = nextKf.value || defaultValue;
      
      // Object interpolation
      if (typeof prevVal === 'object' && prevVal !== null) {
        const result = {};
        for (const key in prevVal) {
          if (typeof prevVal[key] === 'number' && typeof nextVal[key] === 'number') {
            result[key] = prevVal[key] + (nextVal[key] - prevVal[key]) * t;
          } else if (typeof prevVal[key] === 'boolean') {
            // For booleans, use the previous value until we reach the next keyframe
            result[key] = t < 0.5 ? prevVal[key] : nextVal[key];
          } else {
            result[key] = prevVal[key];
          }
        }
        return result;
      }
      
      // Number interpolation
      if (typeof prevVal === 'number') {
        return prevVal + (nextVal - prevVal) * t;
      }
      
      return prevVal;
    };
    
    // Check if we have keyframes for each track type
    const hasPosition = transformKfs.some(kf => kf.trackType === 'position');
    const hasRotation = transformKfs.some(kf => kf.trackType === 'rotation');
    const hasScale = transformKfs.some(kf => kf.trackType === 'scale');
    const hasVisibility = transformKfs.some(kf => kf.trackType === 'visibility');
    
    return {
      position: hasPosition ? interpolateTrack('position', { x: 0, y: 0, z: 0 }) : null,
      rotation: hasRotation ? interpolateTrack('rotation', { x: 0, y: 0, z: 0 }) : null,
      scale: hasScale ? interpolateTrack('scale', { x: 1, y: 1, z: 1 }) : null,
      visibility: hasVisibility ? interpolateTrack('visibility', { visible: true, opacity: 1 }) : null
    };
  }, [activeClip, currentFrame]);
  
  /* ============== OLD ROOT MOTION CODE (COMMENTED OUT) ==============
   * Keeping for reference - this used constant velocity which caused foot sliding
   *
  const rootMotionOffset_OLD = useMemo(() => {
    if (!activeClip || !selectedCharacter) {
      return { x: 0, y: 0, z: 0 };
    }
    
    const clipCharacterId = activeClip.characterId || selectedCharacter.id;
    const transform = characterTransforms[clipCharacterId];
    const rootMotionEnabled = transform?.rootMotionEnabled ?? true;
    
    if (!rootMotionEnabled) {
      return { x: 0, y: 0, z: 0 };
    }
    
    const animation = activeClip.animation;
    const rootMotion = animation?.rootMotion;
    const rootMotionMode = transform?.rootMotionMode || 'dpc';
    
    const localFrame = currentFrame - activeClip.startFrame;
    const speed = activeClip.speed || 1;
    const effectiveFrame = localFrame * speed;
    const baseDuration = animation?.total_frames || 48;
    
    const rotationRad = THREE.MathUtils.degToRad(transform?.rotation?.y || 0);
    
    // PRECISE MODE - used hardcoded distance (caused foot sliding)
    if (rootMotionMode === 'precise') {
      const preciseDistance = 1.3;
      const totalCycles = effectiveFrame / baseDuration;
      const completedCycles = Math.floor(totalCycles);
      const cycleProgress = totalCycles - completedCycles;
      const fourPi = Math.PI * 4;
      const variationStrength = 0.4;
      const cumulativeProgress = cycleProgress - (variationStrength / fourPi) * Math.sin(fourPi * cycleProgress);
      const totalDistance = (completedCycles + cumulativeProgress) * preciseDistance;
      const offsetX = Math.sin(rotationRad) * totalDistance;
      const offsetZ = Math.cos(rotationRad) * totalDistance;
      return { x: offsetX, y: 0, z: offsetZ };
    }
    
    // DPC MODE - user adjustable distance (also caused sliding)
    const userDistance = transform?.rootMotionDistance;
    const animationDistance = rootMotion?.distancePerCycle || 1.3;
    const distancePerCycle = userDistance !== undefined ? userDistance : animationDistance;
    
    if (distancePerCycle === 0) {
      return { x: 0, y: 0, z: 0 };
    }
    
    const totalCycles = effectiveFrame / baseDuration;
    const distance = totalCycles * distancePerCycle;
    const offsetX = Math.sin(rotationRad) * distance;
    const offsetZ = Math.cos(rotationRad) * distance;
    
    return { x: offsetX, y: 0, z: offsetZ };
  }, [activeClip, currentFrame, selectedCharacter, characterTransforms]);
  ============== END OLD ROOT MOTION CODE ============== */

  // Check if we should use built-in animation
  // ONLY use built-in if there are no keyframes (keyframes take priority)
  useEffect(() => {
    const hasKeyframes = activeClip?.animation?.keyframes?.length > 0;
    const wantsBuiltIn = activeClip?.animation?.useBuiltIn;
    
    // Keyframes take priority - if we have them, don't use built-in
    if (hasKeyframes) {
      console.log('Using keyframes instead of built-in animation');
      setUseBuiltInAnimation(false);
    } else if (wantsBuiltIn) {
      console.log('Using built-in animation (no keyframes)');
      setUseBuiltInAnimation(true);
    } else {
      setUseBuiltInAnimation(false);
    }
  }, [activeClip]);

  // Handlers
  const handleCharacterCreated = () => loadCharacters();
  
  const handleDeleteCharacter = async (character) => {
    if (!window.confirm(`Are you sure you want to delete "${character.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/api/3d-lab/project/${character.id}`);
      if (selectedCharacter?.id === character.id) {
        setSelectedCharacter(null);
      }
      loadCharacters();
      toast.success(`Character "${character.name}" deleted`);
    } catch (err) {
      console.error('Failed to delete character:', err);
      toast.error('Failed to delete character');
    }
  };

  const handleSelectCharacter = (character) => {
    setSelectedCharacter(character);
    setSelectedClip(null);
    setExpandedClipId(null);
    
    // Auto-add to timeline if not already there
    setTimelineCharacters(prev => {
      if (!prev.find(c => c.id === character.id)) {
        return [...prev, character];
      }
      return prev;
    });
    
    // Also select this character on the timeline so animations can be added
    setSelectedTimelineCharacter(character);
  };
  
  // Add character to timeline (for drag-and-drop)
  const handleAddCharacterToTimeline = (character) => {
    // Check if character is ready
    if (!['styled', 'rigged', 'model_ready'].includes(character.status)) {
      toast.error('Character must be ready before adding to timeline');
      return;
    }
    
    setTimelineCharacters(prev => {
      if (prev.find(c => c.id === character.id)) {
        toast.info(`${character.name} is already on the timeline`);
        return prev;
      }
      toast.success(`Added ${character.name} to timeline`);
      return [...prev, character];
    });
    
    // Initialize transform for new character
    setCharacterTransforms(prev => ({
      ...prev,
      [character.id]: {
        position: { x: 0, y: 0, z: 0 },
        rotation: { y: 0 },
        scale: 1,
        visible: true,
        rootMotionEnabled: true,
        rootMotionDistance: 1.3, // Default distance per walk cycle
        rootMotionMode: 'precise' // 'precise' (variable velocity) or 'dpc' (constant velocity)
      }
    }));
  };
  
  // Handle character selection in timeline (click on character name)
  const handleSelectTimelineCharacter = (character) => {
    setSelectedTimelineCharacter(character);
    setSelectedClip(null); // Deselect clip when selecting character
  };
  
  // Handle character transform change
  const handleCharacterTransformChange = useCallback((characterId, transform) => {
    setCharacterTransforms(prev => ({
      ...prev,
      [characterId]: transform
    }));
  }, []);
  
  // Handle root motion toggle
  const handleRootMotionChange = useCallback((characterId, enabled) => {
    setCharacterTransforms(prev => ({
      ...prev,
      [characterId]: {
        ...prev[characterId],
        rootMotionEnabled: enabled
      }
    }));
  }, []);
  
  // Handle root motion distance change
  const handleRootMotionDistanceChange = useCallback((characterId, distance) => {
    setCharacterTransforms(prev => ({
      ...prev,
      [characterId]: {
        ...prev[characterId],
        rootMotionDistance: distance
      }
    }));
  }, []);
  
  // Handle root motion mode change (DPC vs Precise)
  const handleRootMotionModeChange = useCallback((characterId, mode) => {
    setCharacterTransforms(prev => ({
      ...prev,
      [characterId]: {
        ...prev[characterId],
        rootMotionMode: mode
      }
    }));
  }, []);
  
  // Add transform keyframe for character position/rotation animation
  const handleAddTransformKeyframe = useCallback((characterId, frame, transform) => {
    setCharacterTransformKeyframes(prev => {
      const existing = prev[characterId] || [];
      // Remove any existing keyframe at this frame
      const filtered = existing.filter(kf => kf.frame !== frame);
      // Add new keyframe
      const newKeyframes = [...filtered, {
        id: `transform-kf-${Date.now()}`,
        frame,
        position: { ...transform.position },
        rotation: { ...transform.rotation },
        scale: transform.scale
      }].sort((a, b) => a.frame - b.frame);
      
      return {
        ...prev,
        [characterId]: newKeyframes
      };
    });
    toast.success(`Added transform keyframe at frame ${frame}`);
  }, []);
  
  // ============== SHOT SETTINGS HANDLERS ==============
  
  // Handle shot settings change (camera/light position/rotation)
  const handleShotSettingsChange = useCallback((trackType, settings) => {
    setShotSettings(prev => ({
      ...prev,
      [trackType]: { ...prev[trackType], ...settings }
    }));
  }, []);
  
  // Add shot keyframe (camera or light)
  const handleShotKeyframeAdd = useCallback((trackType, frame, value) => {
    setShotKeyframes(prev => {
      const existing = prev[trackType] || [];
      // Remove any existing keyframe at this frame
      const filtered = existing.filter(kf => kf.frame !== frame);
      // Add new keyframe
      const newKeyframes = [...filtered, { frame, value }].sort((a, b) => a.frame - b.frame);
      return { ...prev, [trackType]: newKeyframes };
    });
  }, []);
  
  // Delete shot keyframe
  const handleShotKeyframeDelete = useCallback((trackType, frame) => {
    setShotKeyframes(prev => {
      const existing = prev[trackType] || [];
      const filtered = existing.filter(kf => kf.frame !== frame);
      return { ...prev, [trackType]: filtered };
    });
  }, []);
  
  // Update shot keyframe (move to new frame or update value)
  const handleShotKeyframeUpdate = useCallback((trackType, oldFrame, newFrame, value) => {
    setShotKeyframes(prev => {
      const existing = prev[trackType] || [];
      const updated = existing.map(kf => {
        if (kf.frame === oldFrame) {
          return { frame: newFrame, value };
        }
        return kf;
      }).sort((a, b) => a.frame - b.frame);
      return { ...prev, [trackType]: updated };
    });
  }, []);
  
  // Get interpolated shot values at current frame
  const interpolatedShotSettings = useMemo(() => {
    const result = { camera: null, light: null };
    
    ['camera', 'light'].forEach(trackType => {
      const keyframes = shotKeyframes[trackType] || [];
      if (keyframes.length === 0) {
        result[trackType] = shotSettings[trackType];
        return;
      }
      
      // Find surrounding keyframes
      let prevKf = null;
      let nextKf = null;
      for (const kf of keyframes) {
        if (kf.frame <= currentFrame) prevKf = kf;
        if (kf.frame >= currentFrame && !nextKf) nextKf = kf;
      }
      
      if (!prevKf && !nextKf) {
        result[trackType] = shotSettings[trackType];
      } else if (!prevKf) {
        result[trackType] = nextKf.value;
      } else if (!nextKf || prevKf.frame === nextKf.frame) {
        result[trackType] = prevKf.value;
      } else {
        // Interpolate between keyframes
        const t = (currentFrame - prevKf.frame) / (nextKf.frame - prevKf.frame);
        const interpolateValue = (a, b) => a + (b - a) * t;
        const interpolateObj = (objA, objB) => {
          const result = {};
          for (const key in objA) {
            if (typeof objA[key] === 'object') {
              result[key] = interpolateObj(objA[key], objB[key] || objA[key]);
            } else if (typeof objA[key] === 'number') {
              result[key] = interpolateValue(objA[key], objB[key] ?? objA[key]);
            } else {
              result[key] = objA[key];
            }
          }
          return result;
        };
        result[trackType] = interpolateObj(prevKf.value, nextKf.value);
      }
    });
    
    return result;
  }, [shotKeyframes, shotSettings, currentFrame]);
  
  // Analyze root motion for selected clip (runs automatically when clip is selected)
  // Uses REAL bone data from the loaded model via ThreeCanvas ref
  const analyzeRootMotionForClip = useCallback(async (clip) => {
    if (!clip || clip.animation?.is_blank_template || clip.animation?.rootMotionBaked) {
      setRootMotionAnalysis(null);
      return;
    }
    
    // Only analyze stock/builtin animations that need baking
    if (!clip.animation?.is_stock && !clip.animation?.is_builtin) {
      setRootMotionAnalysis(null);
      return;
    }
    
    setIsAnalyzingRootMotion(true);
    setRootMotionAnalysis(null);
    
    try {
      const totalFrames = clip.animation?.total_frames || 48;
      
      // Get REAL bone position data from ThreeCanvas
      let boneData = null;
      if (canvasRef.current?.sampleRootMotionData) {
        boneData = canvasRef.current.sampleRootMotionData(totalFrames);
      }
      
      if (!boneData) {
        console.warn('Could not sample bone data from ThreeCanvas');
        // Fall back to default analysis
        setRootMotionAnalysis({
          needsBaking: true,
          reason: 'Could not analyze - using defaults',
          strideLength: 1.3,
          footContacts: { leftPlants: [], rightPlants: [] },
          bakedPositions: null
        });
        setCurrentAnalysisStride(1.3);
        setIsAnalyzingRootMotion(false);
        return;
      }
      
      console.log('Got real bone data:', boneData);
      
      // Use the REAL analyzer with actual bone positions
      const result = analyzeAndBakeRootMotion({
        hipPositions: boneData.hipPositions,
        leftFootPositions: boneData.leftFootPositions,
        rightFootPositions: boneData.rightFootPositions,
        totalFrames: boneData.totalFrames
      });
      
      console.log('Real root motion analysis result:', result);
      
      setRootMotionAnalysis(result);
      setCurrentAnalysisStride(result.strideLength || 1.3);
      
    } catch (err) {
      console.error('Root motion analysis error:', err);
      toast.error('Failed to analyze animation');
    } finally {
      setIsAnalyzingRootMotion(false);
    }
  }, []);
  
  // Re-analyze when stride changes - scale baked positions proportionally
  const handleStrideChange = useCallback((newStride) => {
    // Enforce minimum stride of 0.5m
    const clampedStride = Math.max(newStride, 0.5);
    setCurrentAnalysisStride(clampedStride);
    
    if (rootMotionAnalysis?.bakedPositions && rootMotionAnalysis.bakedPositions.length > 0) {
      // Scale existing baked positions to new stride
      const currentStride = rootMotionAnalysis.strideLength || 1.3;
      const scaleFactor = clampedStride / currentStride;
      
      const bakedPositions = rootMotionAnalysis.bakedPositions.map(pos => ({
        frame: pos.frame,
        x: pos.x * scaleFactor,
        y: pos.y, // Y doesn't scale with stride
        z: pos.z * scaleFactor
      }));
      
      setRootMotionAnalysis(prev => ({
        ...prev,
        strideLength: clampedStride,
        bakedPositions
      }));
    }
  }, [rootMotionAnalysis]);
  
  // Handle root motion bake (inline)
  const handleBakeRootMotion = useCallback((stride) => {
    if (!selectedClip || !rootMotionAnalysis) return;
    
    // Use provided stride or fall back to analysis stride, with minimum 0.5m
    const finalStride = Math.max(stride || currentAnalysisStride, 0.5);
    const analysisStride = rootMotionAnalysis.strideLength || 1.3;
    const totalFrames = selectedClip.animation?.total_frames || 48;
    
    // Scale the pre-calculated positions if stride changed
    const scaleFactor = finalStride / analysisStride;
    
    let bakedPositions;
    if (rootMotionAnalysis.bakedPositions && rootMotionAnalysis.bakedPositions.length > 0) {
      // Use pre-calculated positions, scaled to match user's stride
      bakedPositions = rootMotionAnalysis.bakedPositions.map(pos => ({
        frame: pos.frame,
        x: pos.x * scaleFactor,
        y: pos.y, // Y (bobbing) doesn't scale with stride
        z: pos.z * scaleFactor
      }));
    } else {
      // Fallback to linear if no baked positions available
      bakedPositions = [];
      for (let frame = 0; frame < totalFrames; frame++) {
        bakedPositions.push({
          frame,
          x: 0,
          y: 0,
          z: (frame / (totalFrames - 1)) * finalStride
        });
      }
    }
    
    // Generate animation key for persistence
    const animation = selectedClip.animation;
    const animationKey = animation?.id || animation?.name?.toLowerCase().replace(/\s+/g, '_') || `clip_${selectedClip.id}`;
    
    // Update the clip's animation with baked root motion data
    setClips(prev => prev.map(c => {
      if (c.id === selectedClip.id) {
        return {
          ...c,
          animation: {
            ...c.animation,
            rootMotionBaked: true,
            bakedRootMotionPositions: bakedPositions,
            strideLength: finalStride,
            footContacts: rootMotionAnalysis.footContacts
          }
        };
      }
      return c;
    }));
    
    // Update selected clip too
    setSelectedClip(prev => ({
      ...prev,
      animation: {
        ...prev.animation,
        rootMotionBaked: true,
        bakedRootMotionPositions: bakedPositions,
        strideLength: finalStride,
        footContacts: rootMotionAnalysis.footContacts
      }
    }));
    
    // ===== PERSIST TO BACKEND =====
    saveBakedRootMotion(animationKey, {
      positions: bakedPositions,
      strideLength: finalStride,
      totalFrames: totalFrames,
      fps: 24,
      footContacts: rootMotionAnalysis.footContacts
    });
    
    setRootMotionAnalysis(null);
    toast.success(`Root motion baked with ${finalStride.toFixed(2)}m stride`);
  }, [selectedClip, rootMotionAnalysis, currentAnalysisStride, saveBakedRootMotion]);
  
  // Handle unbaking root motion (reset to re-analyze)
  const handleUnbakeRootMotion = useCallback(async () => {
    if (!selectedClip) return;
    
    const animation = selectedClip.animation;
    const animationKey = animation?.id || animation?.name?.toLowerCase().replace(/\s+/g, '_') || `clip_${selectedClip.id}`;
    
    // Remove baked data from the clip
    const updatedClip = {
      ...selectedClip,
      animation: {
        ...selectedClip.animation,
        rootMotionBaked: false,
        bakedRootMotionPositions: null,
        strideLength: null,
        footContacts: null
      }
    };
    
    // Update clips state
    setClips(prev => prev.map(c => c.id === selectedClip.id ? updatedClip : c));
    setSelectedClip(updatedClip);
    
    // Remove from local baked data state
    setBakedRootMotionData(prev => {
      const newData = { ...prev };
      delete newData[animationKey];
      return newData;
    });
    
    // Delete from backend
    if (selectedCharacter?.id) {
      try {
        await axios.delete(`${API}/api/3d-lab/project/${selectedCharacter.id}/baked-root-motion/${animationKey}`);
        console.log(`Deleted baked root motion for: ${animationKey}`);
      } catch (err) {
        console.error('Failed to delete baked root motion from backend:', err);
      }
    }
    
    // Trigger re-analysis
    toast.info('Root motion reset - re-analyzing...');
    
    // Re-analyze after a short delay to allow state to update
    setTimeout(() => {
      analyzeRootMotionForClip(updatedClip);
    }, 100);
    
  }, [selectedClip, selectedCharacter?.id, analyzeRootMotionForClip]);
  
  // Handle root motion mode change (toggle between locomotion and treadmill)
  const handleSetRootMotionMode = useCallback((mode) => {
    if (!selectedClip) return;
    
    const characterId = selectedClip.characterId;
    setCharacterTransforms(prev => ({
      ...prev,
      [characterId]: {
        ...prev[characterId],
        rootMotionMode: mode
      }
    }));
    
    if (mode === 'treadmill') {
      toast.info('Treadmill mode - character animates in place');
    } else {
      toast.info('Locomotion mode - character moves forward');
    }
  }, [selectedClip]);
  
  // Get current root motion mode for selected character
  const currentRootMotionMode = useMemo(() => {
    if (!selectedClip) return 'normal';
    const characterId = selectedClip.characterId;
    return characterTransforms[characterId]?.rootMotionMode || 'normal';
  }, [selectedClip, characterTransforms]);
  
  // Auto-analyze when clip is selected
  useEffect(() => {
    if (selectedClip) {
      analyzeRootMotionForClip(selectedClip);
    } else {
      setRootMotionAnalysis(null);
    }
  }, [selectedClip?.id, analyzeRootMotionForClip]);

  const handleClipChange = (updatedClip) => {
    setClips(prev => prev.map(c => c.id === updatedClip.id ? updatedClip : c));
    setSelectedClip(updatedClip);
  };

  const handleDeleteClip = (clipId) => {
    setClips(prev => prev.filter(c => c.id !== clipId));
    if (selectedClip?.id === clipId) {
      setSelectedClip(null);
    }
    if (expandedClipId === clipId) {
      setExpandedClipId(null);
    }
  };

  const handleDuplicateClip = (clip) => {
    const newClip = {
      ...clip,
      id: `clip-${Date.now()}`,
      startFrame: clip.startFrame + clip.duration
    };
    setClips(prev => [...prev, newClip]);
    toast.success('Clip duplicated');
  };

  const handleClipDoubleClick = (clip) => {
    // Toggle expanded view for bone editing
    setExpandedClipId(prev => prev === clip.id ? null : clip.id);
    setSelectedClip(clip);
  };

  // Handle bones being loaded from model
  const handleBonesLoaded = useCallback((boneNames) => {
    setBones(boneNames);
    console.log('Bones loaded:', boneNames.length);
  }, []);

  const handleBoneTransformChange = useCallback((boneName, transform) => {
    if (canvasRef?.current) {
      canvasRef.current.setBoneTransform(boneName, transform);
    }
    setCurrentBoneTransforms(prev => ({ ...prev, [boneName]: transform }));
  }, []);

  // Handle real-time bone data updates from ThreeCanvas animation loop
  // This feeds the SpriteRenderer with current bone positions/rotations
  const handleBoneDataUpdate = useCallback((boneData) => {
    if (!selectedCharacter?.id) return;
    
    // Debug logging
    const boneCount = Object.keys(boneData || {}).length;
    if (boneCount > 0 && !window._boneDataLogged) {
      window._boneDataLogged = true;
      console.log('[2D Sprite Debug] Bone data received:', boneCount, 'bones');
    }
    
    // Update currentBoneTransforms with character ID key for multi-character support
    setCurrentBoneTransforms(prev => ({
      ...prev,
      [selectedCharacter.id]: boneData
    }));
  }, [selectedCharacter?.id]);

  const handleBoneReset = useCallback((boneName) => {
    if (canvasRef?.current) {
      canvasRef.current.resetBone(boneName);
      const transforms = canvasRef.current.getAllBoneTransforms();
      setCurrentBoneTransforms(transforms);
    }
  }, []);

  // Handle clips change - extract keyframes for built-in animations (Walk, Run, etc.)
  const handleClipsChange = useCallback(async (newClips) => {
    console.log('handleClipsChange called with', newClips.length, 'clips');
    
    // Check if any new clips need keyframe extraction
    const clipsNeedingExtraction = newClips.filter(clip => {
      const needsExtraction = clip.animation?.useBuiltIn && 
        !clip.animation?.keyframes?.length &&
        !clip._extracting;
      console.log(`Clip ${clip.animation?.name}: useBuiltIn=${clip.animation?.useBuiltIn}, keyframes=${clip.animation?.keyframes?.length}, extracting=${clip._extracting}, needs=${needsExtraction}`);
      return needsExtraction;
    });
    
    console.log('Clips needing extraction:', clipsNeedingExtraction.length);
    console.log('Canvas ref extractAnimationKeyframes:', !!canvasRef.current?.extractAnimationKeyframes);
    console.log('Canvas ref extractAnimationFromUrl:', !!canvasRef.current?.extractAnimationFromUrl);
    
    if (clipsNeedingExtraction.length > 0 && (canvasRef.current?.extractAnimationKeyframes || canvasRef.current?.extractAnimationFromUrl)) {
      // Mark clips as extracting
      const markedClips = newClips.map(clip => {
        if (clipsNeedingExtraction.find(c => c.id === clip.id)) {
          return { ...clip, _extracting: true };
        }
        return clip;
      });
      setClips(markedClips);
      
      // Extract keyframes for each clip that needs it
      setTimeout(async () => {
        for (const clipToExtract of clipsNeedingExtraction) {
          // Determine animation type from clip name (Walk -> walk, Run -> run, Jab -> jab)
          const animName = clipToExtract.animation?.name?.toLowerCase() || 'walk';
          const animationType = animName === 'walk' ? 'walk' : 
                               animName === 'run' ? 'run' :
                               animName === 'jump' ? 'jump' :
                               animName === 'punch' ? 'punch' :
                               animName === 'kick' ? 'kick' :
                               animName === 'jab' ? 'jab' :
                               animName === 'block' ? 'block' :
                               animName === 'combo' ? 'combo' :
                               animName === 'wave' ? 'wave' :
                               animName === 'dance' ? 'dance' :
                               animName === 'idle' ? 'idle' : 'walk';
          
          // Use the animation's defined total_frames
          const totalFrames = clipToExtract.animation?.total_frames || (animationType === 'run' ? 24 : 48);
          
          let extracted = null;
          
          // For non-walk animations, load the specific animation GLTF from the backend
          if (animationType !== 'walk' && selectedCharacter?.id) {
            const animationUrl = `${API}/api/3d-lab/proxy-model/${selectedCharacter.id}?type=armature&animation=${animationType}`;
            console.log(`Loading ${animationType} animation from: ${animationUrl}`);
            
            // Use extractAnimationFromUrl to load and extract from the specific animation GLTF
            if (canvasRef.current?.extractAnimationFromUrl) {
              try {
                extracted = await canvasRef.current.extractAnimationFromUrl(animationUrl, 24, totalFrames, animationType);
                console.log(`Extracted keyframes for ${animationType}:`, extracted?.keyframes?.length || 0);
              } catch (err) {
                console.error(`Error extracting ${animationType} animation:`, err);
              }
            } else {
              console.warn('extractAnimationFromUrl not available on canvasRef');
            }
          } else {
            // For walk, use the currently loaded model's animation
            if (canvasRef.current?.extractAnimationKeyframes) {
              extracted = await canvasRef.current.extractAnimationKeyframes(24, totalFrames, animationType);
            }
          }
          
          if (extracted?.keyframes) {
            setClips(prev => prev.map(clip => {
              if (clip.id === clipToExtract.id) {
                return {
                  ...clip,
                  _extracting: false,
                  animation: {
                    ...clip.animation,
                    keyframes: extracted.keyframes,
                    rootMotion: extracted.rootMotion
                  }
                };
              }
              return clip;
            }));
            toast.success(`Keyframes extracted for ${clipToExtract.animation?.name || 'animation'}`);
          } else {
            // Mark as failed extraction
            setClips(prev => prev.map(clip => {
              if (clip.id === clipToExtract.id) {
                return { ...clip, _extracting: false };
              }
              return clip;
            }));
            toast.error(`Failed to extract keyframes for ${clipToExtract.animation?.name || 'animation'}`);
          }
        }
      }, 500);
    } else {
      setClips(newClips);
    }
  }, [selectedCharacter]);

  // Get keyframes for expanded clip
  const getClipKeyframes = useCallback((clipId) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip?.animation?.keyframes) return [];
    
    // Return keyframes relative to frame 0 (local to clip)
    return clip.animation.keyframes;
  }, [clips]);

  // Get current track values for the selected clip at current frame
  const getCurrentTrackValues = useCallback(() => {
    // Return shot settings with live values taking priority
    // Priority: liveShotValues > interpolatedShotSettings > shotSettings (base)
    const shotValues = {
      camera: liveShotValues.camera || interpolatedShotSettings.camera || shotSettings.camera,
      light: liveShotValues.light || interpolatedShotSettings.light || shotSettings.light
    };
    
    if (!selectedClip) return { shot: shotValues };
    
    const animation = selectedClip.animation;
    if (!animation) return { shot: shotValues };
    
    // Calculate local frame within clip
    const localFrame = currentFrame - selectedClip.startFrame;
    if (localFrame < 0) return { shot: shotValues };
    
    // Get transform keyframes
    const transformKfs = animation.transformKeyframes || [];
    const yOffsetKfs = animation.rootMotion?.yOffsetKeyframes || [];
    const boneKfs = animation.keyframes || [];
    
    // Interpolate transform values at current frame
    const interpolateValue = (keyframes, frame, defaultVal) => {
      if (!keyframes || keyframes.length === 0) return defaultVal;
      
      // Find surrounding keyframes
      let prevKf = keyframes[0];
      let nextKf = keyframes[keyframes.length - 1];
      
      for (let i = 0; i < keyframes.length - 1; i++) {
        if (keyframes[i].frame <= frame && keyframes[i + 1].frame >= frame) {
          prevKf = keyframes[i];
          nextKf = keyframes[i + 1];
          break;
        }
      }
      
      // Interpolate
      const range = nextKf.frame - prevKf.frame;
      if (range === 0) return prevKf.value || prevKf.offset || defaultVal;
      
      const t = (frame - prevKf.frame) / range;
      const prevVal = prevKf.value || prevKf.offset || defaultVal;
      const nextVal = nextKf.value || nextKf.offset || defaultVal;
      
      if (typeof prevVal === 'number') {
        return prevVal + (nextVal - prevVal) * t;
      }
      
      // Object interpolation (for position, rotation, scale)
      if (typeof prevVal === 'object') {
        const result = {};
        for (const key in prevVal) {
          result[key] = prevVal[key] + ((nextVal[key] || 0) - prevVal[key]) * t;
        }
        return result;
      }
      
      return prevVal;
    };
    
    // Get Y offset at current frame
    const yOffset = interpolateValue(yOffsetKfs, localFrame, 0);
    
    // Get transform values
    const transforms = {};
    for (const trackType of ['position', 'rotation', 'scale', 'visibility']) {
      const trackKfs = transformKfs.filter(kf => kf.trackType === trackType);
      if (trackKfs.length > 0) {
        transforms[trackType] = interpolateValue(trackKfs, localFrame, 
          trackType === 'scale' ? { x: 1, y: 1, z: 1 } :
          trackType === 'visibility' ? { visible: true, opacity: 1 } :
          { x: 0, y: 0, z: 0 }
        );
      }
    }
    
    // Get bone values at current frame
    const bones = {};
    if (boneKfs.length > 0) {
      // Find keyframe at or before current frame
      const kf = boneKfs.find(k => k.frame === Math.floor(localFrame)) || boneKfs[0];
      if (kf?.bones) {
        Object.assign(bones, kf.bones);
      }
    }
    
    return { transforms, yOffset, bones, shot: shotValues };
  }, [selectedClip, currentFrame, interpolatedShotSettings, shotSettings, liveShotValues]);

  // Handle live track value changes (for preview)
  const handleTrackValueChange = useCallback((track, value) => {
    // Update live edit values for real-time preview
    if (track.type === 'transform') {
      setLiveEditValues(prev => ({
        ...prev,
        [track.id]: value
      }));
    } else if (track.type === 'yOffset') {
      setLiveEditValues(prev => ({
        ...prev,
        yOffset: value
      }));
    } else if (track.type === 'shot') {
      // Update live shot values for real-time preview (Camera/Light)
      setLiveShotValues(prev => ({
        ...prev,
        [track.id]: value
      }));
    }
  }, []);

  // Handle adding a keyframe for any track type
  const handleAddTrackKeyframe = useCallback((track, frame, value, mode) => {
    // Shot tracks (Camera/Light) are handled separately - not tied to clips
    if (track.type === 'shot') {
      handleShotKeyframeAdd(track.id, frame, value);
      // Clear live shot value after keyframe is added (falls back to interpolated)
      setLiveShotValues(prev => ({
        ...prev,
        [track.id]: null
      }));
      toast.success(`${track.name} keyframe added at frame ${frame}`);
      return;
    }
    
    if (!selectedClip) {
      toast.error('No clip selected');
      return;
    }
    
    const clipId = selectedClip.id;
    const localFrame = frame - selectedClip.startFrame;
    
    if (localFrame < 0 || localFrame > selectedClip.duration) {
      toast.error('Frame is outside clip bounds');
      return;
    }
    
    setClips(prevClips => {
      return prevClips.map(clip => {
        if (clip.id !== clipId) return clip;
        
        const animation = { ...clip.animation };
        
        if (track.type === 'yOffset') {
          // Add/update Y offset keyframe
          const yOffsetKfs = [...(animation.rootMotion?.yOffsetKeyframes || [])];
          const existingIdx = yOffsetKfs.findIndex(kf => kf.frame === localFrame);
          
          if (existingIdx >= 0) {
            yOffsetKfs[existingIdx] = { frame: localFrame, offset: value };
          } else {
            yOffsetKfs.push({ frame: localFrame, offset: value });
            yOffsetKfs.sort((a, b) => a.frame - b.frame);
          }
          
          animation.rootMotion = {
            ...animation.rootMotion,
            yOffsetKeyframes: yOffsetKfs
          };
          
        } else if (track.type === 'transform') {
          // Add/update transform keyframe
          const transformKfs = [...(animation.transformKeyframes || [])];
          const existingIdx = transformKfs.findIndex(
            kf => kf.frame === localFrame && kf.trackType === track.id
          );
          
          const newKf = {
            id: `transform-${track.id}-${localFrame}-${Date.now()}`,
            frame: localFrame,
            trackType: track.id,
            value: value,
            transforms: { [track.id]: value }
          };
          
          if (existingIdx >= 0) {
            transformKfs[existingIdx] = newKf;
          } else {
            transformKfs.push(newKf);
            transformKfs.sort((a, b) => a.frame - b.frame);
          }
          
          animation.transformKeyframes = transformKfs;
          
          // If mode is 'all', also add keyframes for other transform tracks
          if (mode === 'all') {
            const otherTracks = ['position', 'rotation', 'scale', 'visibility'].filter(t => t !== track.id);
            for (const otherTrack of otherTracks) {
              const otherExisting = transformKfs.find(kf => kf.frame === localFrame && kf.trackType === otherTrack);
              if (!otherExisting) {
                const defaultVal = otherTrack === 'scale' ? { x: 1, y: 1, z: 1 } :
                                   otherTrack === 'visibility' ? { visible: true, opacity: 1 } :
                                   { x: 0, y: 0, z: 0 };
                transformKfs.push({
                  id: `transform-${otherTrack}-${localFrame}-${Date.now()}`,
                  frame: localFrame,
                  trackType: otherTrack,
                  value: defaultVal,
                  transforms: { [otherTrack]: defaultVal }
                });
              }
            }
            transformKfs.sort((a, b) => a.frame - b.frame);
            animation.transformKeyframes = transformKfs;
          }
          
        } else if (track.type === 'bone') {
          // Bone keyframes are handled by the existing keyframe system
          console.log('Use "Add Keyframe" button to add bone keyframes');
        }
        
        return { ...clip, animation };
      });
    });
    
    // Clear live edit values after keyframe is added (preview becomes the actual keyframe)
    if (track.type === 'transform') {
      setLiveEditValues(prev => ({
        ...prev,
        [track.id]: null
      }));
    } else if (track.type === 'yOffset') {
      setLiveEditValues(prev => ({
        ...prev,
        yOffset: null
      }));
    }
  }, [selectedClip, handleShotKeyframeAdd]);

  return (
    <div className="h-screen flex bg-slate-950 overflow-hidden">
      {/* Left Panel - Asset Library */}
      <AssetLibrary
        characters={characters}
        selectedCharacter={selectedCharacter}
        onSelectCharacter={handleSelectCharacter}
        onCreateNew={() => setShowCreateModal(true)}
        onRefresh={loadCharacters}
        onDeleteCharacter={handleDeleteCharacter}
        loading={loading}
        animations={savedAnimations}
        builtInAnimations={DEFAULT_ANIMATIONS.essentials}
        animationLibrary={animationLibrary}
        animationSearchQuery={animationSearchQuery}
        onAnimationSearch={(query) => {
          setAnimationSearchQuery(query);
          loadAnimationLibrary(query);
        }}
        onRefreshAnimations={loadAnimationLibrary}
        refreshTrigger={animationLibraryRefresh}
        userId={user?.id}
        currentFrame={currentFrame}
        selectedTimelineCharacter={selectedTimelineCharacter}
        onAddAnimationAtPlayhead={(animation) => {
          console.log('onAddAnimationAtPlayhead called', animation.name, 'is_builtin:', animation.is_builtin);
          if (!selectedTimelineCharacter) {
            toast.error('Select a character first');
            return;
          }
          
          // Custom (blank) template creates a 10-second clip (240 frames at 24fps)
          const isBlankTemplate = animation.is_blank_template;
          const clipDuration = isBlankTemplate ? 240 : (animation.total_frames || 48);
          
          const newClip = {
            id: `clip-${Date.now()}`,
            characterId: selectedTimelineCharacter.id,
            startFrame: currentFrame,
            duration: clipDuration,
            color: isBlankTemplate ? 'bg-amber-600' : 'bg-green-600',
            animation: {
              ...animation,
              total_frames: clipDuration,
              useBuiltIn: animation.is_builtin || false,
              is_blank_template: isBlankTemplate,
              // Blank template starts with no keyframes
              keyframes: isBlankTemplate ? [] : (animation.keyframes || [])
            }
          };
          console.log('New clip created with useBuiltIn:', newClip.animation.useBuiltIn);
          handleClipsChange([...clips, newClip]);
          toast.success(isBlankTemplate 
            ? 'Added blank 10s clip - add keyframes to animate' 
            : `Added "${animation.name}" at playhead`
          );
        }}
        isCollapsed={assetLibraryCollapsed}
        onToggleCollapse={() => setAssetLibraryCollapsed(!assetLibraryCollapsed)}
      />

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Canvas Toolbar */}
        <CanvasToolbar
          character={selectedCharacter}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          camera={camera}
          setCamera={setCamera}
          selectedBone={selectedBone}
          setSelectedBone={setSelectedBone}
          bones={bones}
          showInspector={showInspector}
          setShowInspector={setShowInspector}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          onSave={saveStudioSession}
          isSaving={isSaving}
          lastSaved={lastSaved}
          renderMode={renderMode}
          setRenderMode={setRenderMode}
        />

        {/* Canvas + Right Panel */}
        <div className="flex-1 flex overflow-hidden min-w-0">
          {/* Canvas Area (3D or 2D Sprite) */}
          <div className="flex-1 relative bg-slate-900 min-w-0">
            {!selectedCharacter ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <User className="w-16 h-16 mx-auto mb-3 opacity-30" />
                  <p className="text-lg">No character selected</p>
                  <p className="text-sm mt-1">Select a character from the library or create a new one</p>
                </div>
              </div>
            ) : !isCharacterReady ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 mx-auto mb-3 text-purple-500 animate-spin" />
                  <p className="text-lg text-white">{selectedCharacter.name}</p>
                  <p className="text-sm text-slate-400 mt-1">Processing...</p>
                  <div className="w-48 mx-auto mt-3">
                    <Progress value={selectedCharacter.progress || 20} className="h-2" />
                  </div>
                </div>
              </div>
            ) : renderMode === '2d-sprite' ? (
              /* 2D Sprite Renderer - 3D canvas is hidden but still runs below */
              <div className="absolute inset-0 flex flex-col">
                {/* The 3D ThreeCanvas runs hidden underneath to provide bone data */}
                <div className="absolute inset-0" style={{ visibility: 'hidden' }}>
                  <ThreeCanvas
                    ref={canvasRef}
                    modelUrl={modelUrl}
                    texturedModelUrl={texturedModelUrl}
                    backgroundColor="#1a1a2e"
                    isPlaying={true}
                    animationSpeed={activeClip?.speed || 1}
                    orbitTheta={camera.theta}
                    orbitPhi={camera.phi}
                    distance={camera.distance}
                    fov={camera.fov}
                    showGrid={false}
                    enablePosing={false}
                    useBuiltInAnimation={useBuiltInAnimation}
                    useKeyframeAnimation={useClipKeyframes}
                    keyframes={clipKeyframes}
                    currentFrame={clipLocalFrame}
                    totalFrames={activeClip?.animation?.total_frames || totalFrames}
                    fps={24}
                    onBoneDataUpdate={handleBoneDataUpdate}
                    characterPosition={{
                      x: (liveEditValues.position?.x ?? interpolatedTransform.position?.x ?? (characterTransforms[selectedCharacter?.id]?.position?.x || 0)) + rootMotionOffset.x,
                      y: (liveEditValues.position?.y ?? interpolatedTransform.position?.y ?? (characterTransforms[selectedCharacter?.id]?.position?.y || 0)) + rootMotionOffset.y,
                      z: (liveEditValues.position?.z ?? interpolatedTransform.position?.z ?? (characterTransforms[selectedCharacter?.id]?.position?.z || 0)) + rootMotionOffset.z
                    }}
                    characterRotation={{
                      x: liveEditValues.rotation?.x ?? interpolatedTransform.rotation?.x ?? 0,
                      y: liveEditValues.rotation?.y ?? interpolatedTransform.rotation?.y ?? (characterTransforms[selectedCharacter?.id]?.rotation?.y || 0),
                      z: liveEditValues.rotation?.z ?? interpolatedTransform.rotation?.z ?? 0
                    }}
                    characterScale={{
                      x: liveEditValues.scale?.x ?? interpolatedTransform.scale?.x ?? (characterTransforms[selectedCharacter?.id]?.scale || 1),
                      y: liveEditValues.scale?.y ?? interpolatedTransform.scale?.y ?? (characterTransforms[selectedCharacter?.id]?.scale || 1),
                      z: liveEditValues.scale?.z ?? interpolatedTransform.scale?.z ?? (characterTransforms[selectedCharacter?.id]?.scale || 1)
                    }}
                    characterVisible={true}
                    characterOpacity={1}
                    enableFootLocking={activeClip?.animation?.rootMotionBaked === true}
                    footContactData={activeClip?.animation?.footContacts}
                  />
                </div>
                
                {/* Sprite Canvas - overlays the hidden 3D view */}
                <div className="flex-1 relative flex items-center justify-center bg-slate-950">
                  {/* Debug overlay */}
                  <div className="absolute top-2 left-2 z-50 bg-black/90 text-white text-xs p-2 rounded font-mono max-w-xs">
                    <div>Mode: 2D Sprite</div>
                    <div>Character: {selectedCharacter?.name || 'none'}</div>
                    <div>Config loaded: {spriteConfigs[selectedCharacter?.id] ? 'YES' : 'NO'}</div>
                    <div>Bones: {Object.keys(currentBoneTransforms[selectedCharacter?.id] || {}).length}</div>
                    <div>Parts: {Object.keys(spriteConfigs[selectedCharacter?.id]?.parts || {}).length}</div>
                    <div>Total sprites: {Object.values(spriteConfigs[selectedCharacter?.id]?.parts || {}).reduce((sum, p) => sum + (p.angle_buckets?.length || 0), 0)}</div>
                    <div>Loading: {spriteSetupLoading ? 'YES' : 'NO'}</div>
                  </div>
                  
                  {spriteSetupLoading ? (
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 mx-auto mb-2 text-purple-500 animate-spin" />
                      <p className="text-sm text-slate-400">Loading sprite configuration...</p>
                    </div>
                  ) : selectedCharacter && spriteConfigs[selectedCharacter.id] ? (
                    <SpriteRenderer
                      spriteConfig={spriteConfigs[selectedCharacter.id]}
                      boneData={currentBoneTransforms[selectedCharacter.id] || {}}
                      cameraPosition={{ 
                        x: camera.distance * Math.sin(camera.theta * Math.PI / 180),
                        y: camera.distance * Math.cos(camera.phi * Math.PI / 180),
                        z: camera.distance * Math.cos(camera.theta * Math.PI / 180)
                      }}
                      cameraTarget={{ x: 0, y: 1, z: 0 }}
                      width={800}
                      height={600}
                      backgroundColor="#0a0a0f"
                      scale={1.5}
                      debug={false}
                      editable={spriteEditMode}
                      onOffsetsChange={(offsets) => {
                        console.log('[Sprite Edit] User offsets:', JSON.stringify(offsets, null, 2));
                      }}
                      onPivotsChange={(pivots) => {
                        console.log('[Sprite Edit] User pivots:', JSON.stringify(pivots, null, 2));
                      }}
                      className="max-w-full max-h-full"
                    />
                  ) : (
                    <div className="text-center">
                      <Layers className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400 mb-3">
                        {selectedCharacter ? 'No sprite configuration found' : 'Select a character to configure sprites'}
                      </p>
                      {selectedCharacter && (
                        <Button 
                          onClick={() => {
                            setSpriteSetupCharacterId(selectedCharacter.id);
                            setShowSpriteSetup(true);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Setup Sprites
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Sprite Setup Bar - shows all characters' sprite status */}
                {selectedCharacter && spriteConfigs[selectedCharacter.id] && (
                  <div className="h-12 bg-slate-800 border-t border-slate-700 flex items-center px-3 gap-3">
                    <span className="text-xs text-slate-400">
                      <span className="text-white font-medium">{selectedCharacter.name}:</span>{' '}
                      {Object.keys(spriteConfigs[selectedCharacter.id]?.parts || {}).length} body parts
                    </span>
                    <div className="flex-1" />
                    
                    {/* Edit Mode Toggle */}
                    <Button 
                      size="sm" 
                      variant={spriteEditMode ? "default" : "outline"}
                      onClick={() => setSpriteEditMode(!spriteEditMode)}
                      className={`h-7 text-xs ${spriteEditMode ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-slate-600'}`}
                    >
                      {spriteEditMode ? '✏️ Edit Mode ON' : '▶️ Animation Mode'}
                    </Button>
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSpriteSetupCharacterId(selectedCharacter.id);
                        setShowSpriteSetup(true);
                      }}
                      className="h-7 text-xs border-slate-600"
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Configure Sprites
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* 3D Canvas */
              <ThreeCanvas
                ref={canvasRef}
                modelUrl={modelUrl}
                texturedModelUrl={texturedModelUrl}
                backgroundColor="#1a1a2e"
                isPlaying={isPlaying}
                animationSpeed={activeClip?.speed || 1}
                orbitTheta={camera.theta}
                orbitPhi={camera.phi}
                distance={camera.distance}
                fov={camera.fov}
                showGrid={showGrid}
                enablePosing={true}
                selectedBone={selectedBone}
                onBoneSelect={setSelectedBone}
                onBonesLoaded={handleBonesLoaded}
                onBoneTransformChange={handleBoneTransformChange}
                onBoneDataUpdate={handleBoneDataUpdate}
                useBuiltInAnimation={useBuiltInAnimation}
                useKeyframeAnimation={useClipKeyframes}
                keyframes={clipKeyframes}
                currentFrame={clipLocalFrame}
                totalFrames={activeClip?.animation?.total_frames || totalFrames}
                fps={24}
                characterPosition={{
                  // Priority: liveEditValues > interpolatedTransform > base transform + root motion
                  x: (liveEditValues.position?.x ?? interpolatedTransform.position?.x ?? (characterTransforms[selectedCharacter?.id]?.position?.x || 0)) + rootMotionOffset.x,
                  y: (liveEditValues.position?.y ?? interpolatedTransform.position?.y ?? (characterTransforms[selectedCharacter?.id]?.position?.y || 0)) + rootMotionOffset.y,
                  z: (liveEditValues.position?.z ?? interpolatedTransform.position?.z ?? (characterTransforms[selectedCharacter?.id]?.position?.z || 0)) + rootMotionOffset.z
                }}
                characterRotation={{
                  // Priority: liveEditValues > interpolatedTransform > base rotation
                  x: liveEditValues.rotation?.x ?? interpolatedTransform.rotation?.x ?? 0,
                  y: liveEditValues.rotation?.y ?? interpolatedTransform.rotation?.y ?? (characterTransforms[selectedCharacter?.id]?.rotation?.y || 0),
                  z: liveEditValues.rotation?.z ?? interpolatedTransform.rotation?.z ?? 0
                }}
                characterScale={{
                  // Priority: liveEditValues > interpolatedTransform > base scale
                  x: liveEditValues.scale?.x ?? interpolatedTransform.scale?.x ?? (characterTransforms[selectedCharacter?.id]?.scale || 1),
                  y: liveEditValues.scale?.y ?? interpolatedTransform.scale?.y ?? (characterTransforms[selectedCharacter?.id]?.scale || 1),
                  z: liveEditValues.scale?.z ?? interpolatedTransform.scale?.z ?? (characterTransforms[selectedCharacter?.id]?.scale || 1)
                }}
                characterVisible={
                  // Priority: liveEditValues > interpolatedTransform > base visibility
                  // Use selectedTimelineCharacter.id if available, otherwise selectedCharacter.id
                  liveEditValues.visibility?.visible ?? interpolatedTransform.visibility?.visible ?? (characterTransforms[selectedTimelineCharacter?.id || selectedCharacter?.id]?.visible ?? true)
                }
                characterOpacity={liveEditValues.visibility?.opacity ?? interpolatedTransform.visibility?.opacity ?? (characterTransforms[selectedTimelineCharacter?.id || selectedCharacter?.id]?.opacity ?? 1)}
                enableFootLocking={activeClip?.animation?.rootMotionBaked === true}
                footContactData={activeClip?.animation?.footContacts}
                // Shot settings (camera & light)
                // Priority: liveShotValues > interpolatedShotSettings > shotSettings (base)
                cameraSettings={liveShotValues.camera || interpolatedShotSettings.camera || shotSettings.camera}
                lightSettings={liveShotValues.light || interpolatedShotSettings.light || shotSettings.light}
                // Camera mouse controls - update live shot values for real-time sync
                onCameraChange={handleCameraMouseChange}
              />
            )}
            
            {/* Selected bone indicator */}
            {selectedBone && isCharacterReady && (
              <div className="absolute top-2 left-2 bg-purple-600/90 px-3 py-1.5 rounded-lg text-white text-sm flex items-center gap-2">
                <Bone className="w-4 h-4" />
                {selectedBone}
              </div>
            )}
          </div>

          {/* Right Panel - Clip Settings or Bone Inspector */}
          <div className="w-72 flex-shrink-0 bg-slate-900 border-l border-slate-700">
            {selectedClip || selectedTrack?.type === 'shot' ? (
              <ClipSettingsPanel
                clip={selectedClip}
                onClipChange={handleClipChange}
                onDeleteClip={handleDeleteClip}
                onDuplicateClip={handleDuplicateClip}
                onSetRootMotionMode={handleSetRootMotionMode}
                rootMotionMode={currentRootMotionMode}
                selectedTrack={selectedTrack}
                currentFrame={currentFrame}
                recordMode={recordMode}
                currentTrackValues={getCurrentTrackValues()}
                onTrackValueChange={(track, value) => {
                  // Update live preview
                  handleTrackValueChange(track, value);
                }}
                onAddKeyframe={(track, frame, value, mode) => {
                  handleAddTrackKeyframe(track, frame, value, mode);
                }}
                userId={user?.id}
                userEmail={user?.email}
                onSaveAsSignature={(savedAnimation) => {
                  // Trigger animation library refresh
                  setAnimationLibraryRefresh(prev => prev + 1);
                  toast.success(`Animation "${savedAnimation.name}" saved to your Signature clips!`);
                }}
                className="h-full"
              />
            ) : showInspector && selectedBone ? (
              <BoneInspector
                selectedBone={selectedBone}
                boneTransform={currentBoneTransforms[selectedBone]}
                onRotationChange={handleBoneTransformChange}
                onReset={handleBoneReset}
                bones={bones}
              />
            ) : (
              <div className="h-full flex items-center justify-center p-4">
                <div className="text-center text-slate-500">
                  <SlidersHorizontal className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No clip selected</p>
                  <p className="text-xs mt-1 opacity-70">
                    {clips.length > 0 ? 'Click a clip in the timeline to edit' : 'Add an animation to the timeline'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clip Timeline */}
        <ClipTimeline
          characters={timelineCharacters}
          clips={clips}
          onClipsChange={handleClipsChange}
          selectedClip={selectedClip}
          onSelectClip={(clip) => {
            setSelectedClip(clip);
            setSelectedTimelineCharacter(null);
            setSelectedTrack(null); // Deselect track when selecting clip
          }}
          onClipDoubleClick={handleClipDoubleClick}
          isPlaying={isPlaying}
          onPlayPause={() => setIsPlaying(!isPlaying)}
          currentFrame={currentFrame}
          onFrameChange={setCurrentFrame}
          totalFrames={totalFrames}
          fps={24}
          bones={bones}
          expandedClipId={expandedClipId}
          getClipKeyframes={getClipKeyframes}
          onAddCharacterToTimeline={handleAddCharacterToTimeline}
          onCaptureCurrentPose={() => canvasRef.current?.getAllBoneTransforms?.()}
          onCaptureCharacterTransform={() => {
            // Return current character transform for keyframing
            if (!selectedTimelineCharacter) return null;
            return characterTransforms[selectedTimelineCharacter.id] || {
              position: { x: 0, y: 0, z: 0 },
              rotation: { x: 0, y: 0, z: 0 },
              scale: { x: 1, y: 1, z: 1 },
              visible: true
            };
          }}
          selectedCharacter={selectedTimelineCharacter}
          onSelectCharacter={(char) => {
            setSelectedTimelineCharacter(char);
            setSelectedClip(null);
            setSelectedTrack(null);
          }}
          selectedTrack={selectedTrack}
          onSelectTrack={(track) => {
            setSelectedTrack(track);
            // Don't deselect clip when selecting track
          }}
          recordMode={recordMode}
          onRecordModeChange={setRecordMode}
          // Shot settings (camera & light)
          shotSettings={shotSettings}
          shotKeyframes={shotKeyframes}
          onShotSettingsChange={handleShotSettingsChange}
          onShotKeyframeAdd={handleShotKeyframeAdd}
          onShotKeyframeDelete={handleShotKeyframeDelete}
          onShotKeyframeUpdate={handleShotKeyframeUpdate}
        />
      </div>

      {/* Create Character Modal */}
      <CreateCharacterModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCharacterCreated}
      />
      
      {/* Sprite Setup Dialog */}
      <Dialog open={showSpriteSetup} onOpenChange={setShowSpriteSetup}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-400" />
              2D Sprite Configuration
              {spriteSetupCharacterId && characters.find(c => c.id === spriteSetupCharacterId) && (
                <span className="text-purple-300 font-normal">
                  — {characters.find(c => c.id === spriteSetupCharacterId)?.name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload sprites for each body part at different angles. The 3D skeleton will control which sprites are displayed.
            </DialogDescription>
          </DialogHeader>
          
          {/* Character selector if multiple characters */}
          {timelineCharacters.length > 1 && (
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-700">
              <span className="text-xs text-slate-500">Character:</span>
              <div className="flex gap-1">
                {timelineCharacters.map(tc => {
                  const char = characters.find(c => c.id === tc.characterId);
                  if (!char) return null;
                  return (
                    <Button
                      key={tc.characterId}
                      size="sm"
                      variant={spriteSetupCharacterId === tc.characterId ? 'default' : 'outline'}
                      onClick={() => {
                        setSpriteSetupCharacterId(tc.characterId);
                        setSelectedSpritePart(null);
                        // Load config for this character if needed
                        if (!spriteConfigs[tc.characterId]) {
                          loadOrCreateSpriteConfig(char);
                        }
                      }}
                      className={`h-7 text-xs ${spriteSetupCharacterId === tc.characterId ? 'bg-purple-600' : 'border-slate-600'}`}
                    >
                      {char.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          
          {spriteSetupCharacterId && spriteConfigs[spriteSetupCharacterId] && (
            <div className="flex-1 flex gap-4 overflow-hidden">
              {/* Body Parts List */}
              <div className="w-48 border-r border-slate-700 pr-3">
                <p className="text-xs text-slate-500 mb-2 font-medium">BODY PARTS</p>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-1">
                    {Object.entries(spriteConfigs[spriteSetupCharacterId].parts || {}).map(([partName, partConfig]) => {
                      const spriteCount = partConfig.angle_buckets?.length || 0;
                      const isSelected = selectedSpritePart === partName;
                      return (
                        <button
                          key={partName}
                          onClick={() => setSelectedSpritePart(partName)}
                          className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between transition-colors ${
                            isSelected 
                              ? 'bg-purple-600 text-white' 
                              : 'text-slate-300 hover:bg-slate-800'
                          }`}
                        >
                          <span className="truncate">{partName.replace(/_/g, ' ')}</span>
                          <span className={`text-xs ${isSelected ? 'text-purple-200' : 'text-slate-500'}`}>
                            {spriteCount}/8
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Angle Buckets Grid */}
              <div className="flex-1 overflow-hidden">
                {selectedSpritePart ? (
                  <div>
                    <p className="text-sm text-white mb-3 font-medium capitalize">
                      {selectedSpritePart.replace(/_/g, ' ')} Sprites
                    </p>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { name: 'front', label: 'Front', range: [-22.5, 22.5] },
                        { name: 'quarter_front_right', label: '¼ Front R', range: [22.5, 67.5] },
                        { name: 'right', label: 'Right', range: [67.5, 112.5] },
                        { name: 'quarter_back_right', label: '¼ Back R', range: [112.5, 157.5] },
                        { name: 'back', label: 'Back', range: [157.5, -157.5] },
                        { name: 'quarter_back_left', label: '¼ Back L', range: [-157.5, -112.5] },
                        { name: 'left', label: 'Left', range: [-112.5, -67.5] },
                        { name: 'quarter_front_left', label: '¼ Front L', range: [-67.5, -22.5] },
                      ].map((bucket) => {
                        const existingBucket = spriteConfigs[spriteSetupCharacterId].parts[selectedSpritePart]?.angle_buckets?.find(
                          b => b.name === bucket.name
                        );
                        return (
                          <div key={bucket.name} className="relative">
                            <label className="block aspect-square rounded-lg border-2 border-dashed border-slate-600 hover:border-purple-500 cursor-pointer transition-colors overflow-hidden bg-slate-800/50">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    uploadSpriteForPart(
                                      selectedSpritePart,
                                      bucket.name,
                                      bucket.range[0],
                                      bucket.range[1],
                                      file
                                    );
                                  }
                                }}
                              />
                              {existingBucket?.sprite_url ? (
                                <img 
                                  src={existingBucket.sprite_url} 
                                  alt={bucket.label}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Upload className="w-6 h-6 text-slate-500" />
                                </div>
                              )}
                            </label>
                            <p className="text-xs text-slate-400 text-center mt-1">{bucket.label}</p>
                            {existingBucket && (
                              <button
                                onClick={() => {
                                  // Remove sprite (would need API endpoint)
                                  toast.info('Remove sprite feature coming soon');
                                }}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="w-3 h-3 text-white" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-400">
                        <strong className="text-slate-300">Tip:</strong> Upload PNG images with transparent backgrounds for best results. 
                        Each angle represents 45° of rotation around the character.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Select a body part to configure sprites</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
// Force recompile Sat Feb 14 13:06:09 UTC 2026
